import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import { QRCodeSVG } from "qrcode.react";
import Cropper from "react-easy-crop";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Save,
  Eye,
  Share2,
  QrCode,
  Download,
  Copy,
  Upload,
  Trash2,
  Plus,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Image,
  Video,
  Globe,
  Settings,
  Palette,
  Type,
  Phone,
  Mail,
  AtSign,
  Link as LinkIcon,
  X as XIcon,
  Check,
  ExternalLink,
  CreditCard,
  Unlink,
  Smartphone,
  BarChart3,
  Users,
  MessageSquare,
  FileDown,
  Camera,
  MapPin,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Send,
  RotateCcw,
  Star,
  Lock,
  Film,
  Pencil,
  DollarSign,
  AlertTriangle,
  Calendar,
  Clock,
  ArrowLeft,
} from "lucide-react";
import {
  getProfile,
  updateProfile,
  uploadProfileImage,
  getBlocks,
  createBlock,
  updateBlock,
  deleteBlock,
  reorderBlocks,
  addContactItem,
  updateContactItem,
  deleteContactItem,
  addSocialLink,
  updateSocialLink,
  deleteSocialLink,
  getGalleries,
  createGallery,
  updateGallery,
  deleteGallery,
  uploadMedia,
  deleteMediaItem,
  updateMediaItem,
  reorderMediaItems,
  getProfileCards,
  linkCard,
  unlinkCardUser,
  getLeads,
  updateLead,
  deleteLead as deleteLeadApi,
  subscribePush,
  unsubscribePush,
  resetProfile,
  getReviews,
  uploadBlockMedia,
  deleteBlockMedia,
  getProfileStorage,
} from "../services/api";
import {
  subscribeToPush,
  unsubscribeFromPush,
  getPushSubscription,
  isPushSupported,
} from "../services/pushNotifications";
import { useAppConfig } from "../context/AppConfigContext";
import { useTranslation } from "../i18n/LanguageContext";
import translations from "../i18n/translations";

/* ============================================================
   CONSTANTS
   ============================================================ */

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const TAB_KEYS = [
  { key: "general", tKey: "editor.tab.general", icon: Settings },
  { key: "appearance", tKey: "editor.tab.appearance", icon: Palette },
  { key: "blocks", tKey: "editor.tab.blocks", icon: Type },
  { key: "gallery", tKey: "editor.tab.gallery", icon: Image },
  { key: "share", tKey: "editor.tab.share", icon: Share2 },
  { key: "app", tKey: "editor.tab.app", icon: Smartphone },
];


const BLOCK_TYPE_KEYS = [
  { value: "contact_info", tKey: "editor.blockType.contact_info", icon: Phone },
  { value: "social_links", tKey: "editor.blockType.social_links", icon: Globe },
  { value: "about", tKey: "editor.blockType.about", icon: Type },
  { value: "custom_text", tKey: "editor.blockType.custom_text", icon: Type },
  { value: "cta_button", tKey: "editor.blockType.cta_button", icon: LinkIcon },
  { value: "website", tKey: "editor.blockType.website", icon: ExternalLink },
  // header removed — merged into custom_text with show_title option
  { value: "location", tKey: "editor.blockType.location", icon: Globe },
  { value: "contact_form", tKey: "editor.blockType.contact_form", icon: MessageSquare },
  { value: "gallery", tKey: "editor.blockType.gallery", icon: Image },
  { value: "media", tKey: "editor.blockType.media", icon: Film },
  { value: "bank_info", tKey: "editor.blockType.bank_info", icon: CreditCard },
  { value: "billing_info", tKey: "editor.blockType.billing_info", icon: FileDown },
  { value: "google_review", tKey: "editor.blockType.google_review", icon: Star },
  { value: "map", tKey: "editor.blockType.map", icon: MapPin },
  { value: "payment", tKey: "editor.blockType.payment", icon: DollarSign },
  { value: "appointment", tKey: "editor.blockType.appointment", icon: Calendar },
];

const BLOCK_DEFAULT_TITLE_KEY = {
  contact_info: "public.blockTitle.contact",
  social_links: "public.blockTitle.social",
  about: "public.blockTitle.about",
  location: "public.blockTitle.location",
  website: "public.blockTitle.website",
  cta_button: "public.blockTitle.learnMore",
  bank_info: "public.bank.title",
  billing_info: "public.billing.title",
  contact_form: "contactForm.title",
  google_review: "review.title",
  appointment: "editor.appointment.title",
};

function getBlockWarning(block) {
  const c = block.config || {};
  switch (block.block_type) {
    case "about": return !(c.text_i18n?.en || "").trim() && !(c.text_i18n?.es || "").trim();
    case "location": return !(c.address || "").trim();
    case "map": return !c.lat || !c.lng;
    case "contact_info": return !(block.contact_items || []).some((i) => (i.value || "").trim());
    case "social_links": return !(block.social_links || []).some((l) => (l.url || "").trim());
    case "custom_text":
    case "header": {
      const stripped = (c.content || "").replace(/<[^>]*>/g, "").trim();
      return !stripped && !(c.show_title !== false && (c.title_i18n?.en || c.title_i18n?.es));
    }
    case "cta_button": return !(c.url || "").trim();
    case "website": return !(c.url || "").trim();
    case "bank_info": return !["bank_name", "clabe", "account_number", "card_number", "beneficiary", "phone"].some((k) => (c[k] || "").trim());
    case "billing_info": return !["rfc", "business_name", "zip_code", "tax_regime", "cfdi_use", "billing_email"].some((k) => (c[k] || "").trim());
    case "media": return !c.media_url;
    case "gallery": return !c.gallery_id;
    case "google_review": return !(c.place_id || "").trim();
    case "payment": return !(c.mp_access_token || "").trim();
    default: return false;
  }
}

const CONTACT_TYPES = [
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "whatsapp", label: "WhatsApp" },
];

function WhatsAppIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

const CONTACT_ICONS = {
  email: Mail,
  phone: Phone,
  whatsapp: WhatsAppIcon,
};

function ContactTypeSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = CONTACT_TYPES.find((ct) => ct.value === value) || CONTACT_TYPES[0];
  const Icon = CONTACT_ICONS[current.value] || Mail;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 px-2 py-1.5 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition"
        title={current.label}
      >
        <Icon className="w-4 h-4" />
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </button>
      {open && createPortal(
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
          onClick={() => setOpen(false)}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative bg-white w-full sm:w-80 sm:rounded-xl rounded-t-xl max-h-[70vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between rounded-t-xl">
              <span className="text-sm font-semibold text-gray-700">Select Type</span>
              <button type="button" onClick={() => setOpen(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <XIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="py-1">
              {CONTACT_TYPES.map((ct) => {
                const CtIcon = CONTACT_ICONS[ct.value] || Mail;
                const selected = ct.value === value;
                return (
                  <button
                    key={ct.value}
                    type="button"
                    onClick={() => { onChange(ct.value); setOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition ${
                      selected ? "bg-indigo-50 text-indigo-700 font-medium" : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <CtIcon className="w-5 h-5 flex-shrink-0" />
                    <span className="flex-1 text-left">{ct.label}</span>
                    {selected && <Check className="w-4 h-4 text-indigo-600" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

/* Simple SVG icons for platforms not in lucide-react */
function TikTokIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.87a8.16 8.16 0 004.76 1.52v-3.4a4.85 4.85 0 01-1-.3z" />
    </svg>
  );
}
function PinterestIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0a12 12 0 00-4.37 23.17c-.1-.94-.2-2.4.04-3.44l1.4-5.96s-.36-.72-.36-1.78c0-1.67.97-2.92 2.17-2.92 1.02 0 1.52.77 1.52 1.7 0 1.03-.66 2.58-1 4.01-.28 1.2.6 2.18 1.79 2.18 2.14 0 3.79-2.26 3.79-5.52 0-2.89-2.08-4.9-5.04-4.9-3.43 0-5.45 2.58-5.45 5.24 0 1.04.4 2.15.9 2.75a.36.36 0 01.08.35l-.33 1.36c-.05.22-.18.27-.41.16-1.55-.72-2.52-2.99-2.52-4.81 0-3.92 2.85-7.52 8.2-7.52 4.31 0 7.65 3.07 7.65 7.17 0 4.28-2.7 7.72-6.44 7.72-1.26 0-2.44-.65-2.85-1.43l-.77 2.96c-.28 1.08-1.04 2.44-1.55 3.26A12 12 0 1012 0z" />
    </svg>
  );
}
function SnapchatIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.21 1.5c2.76.02 4.83 1.21 5.99 3.44.56 1.08.68 2.24.62 3.44-.03.58-.1 1.16-.17 1.74-.01.1 0 .18.1.24.52.28 1.05.5 1.62.55.3.02.5.2.5.46 0 .28-.2.48-.52.52-.36.05-.72.03-1.07.1-.33.06-.65.16-.97.3.14.26.3.5.47.74.77 1.06 1.7 1.94 2.83 2.6.4.23.82.38 1.28.42.24.02.4.16.42.38.02.28-.12.48-.36.6-.44.22-.9.36-1.38.46-.3.06-.6.1-.88.2-.14.06-.22.14-.26.3-.08.36-.22.5-.6.56-.36.06-.72.16-1.06.3a5.6 5.6 0 00-1.82 1.24c-.76.76-1.64 1.22-2.72 1.3-1.18.1-2.22-.24-3.16-.9a5.4 5.4 0 00-1.2-.68c-.5-.2-1.02-.3-1.56-.36-.22-.02-.38-.14-.44-.36-.04-.16-.1-.28-.24-.34-.3-.12-.6-.18-.92-.24-.46-.1-.92-.22-1.34-.44-.3-.16-.44-.4-.38-.68.04-.2.18-.34.42-.36.46-.04.88-.2 1.28-.42 1.14-.66 2.06-1.54 2.84-2.6.16-.24.32-.48.46-.74-.32-.14-.64-.24-.98-.3-.34-.06-.7-.04-1.06-.1-.32-.04-.52-.24-.52-.52s.2-.44.5-.46c.56-.04 1.1-.26 1.62-.54.1-.06.12-.14.1-.24-.06-.58-.14-1.16-.16-1.74-.06-1.2.06-2.36.62-3.44C7.38 2.7 9.46 1.52 12.21 1.5z" />
    </svg>
  );
}

const PLATFORM_ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  tiktok: TikTokIcon,
  x: XIcon,
  linkedin: Linkedin,
  youtube: Youtube,
  pinterest: PinterestIcon,
  snapchat: SnapchatIcon,
  telegram: Send,
  website: Globe,
};

const SOCIAL_PLATFORMS = [
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "tiktok", label: "TikTok" },
  { value: "x", label: "X (Twitter)" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "youtube", label: "YouTube" },
  { value: "pinterest", label: "Pinterest" },
  { value: "snapchat", label: "Snapchat" },
  { value: "telegram", label: "Telegram" },
  { value: "website", label: "Website" },
];

function PlatformSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = SOCIAL_PLATFORMS.find((sp) => sp.value === value) || SOCIAL_PLATFORMS[0];
  const Icon = PLATFORM_ICONS[current.value] || Globe;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1 px-2 py-1.5 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition"
        title={current.label}
      >
        <Icon className="w-4 h-4" />
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </button>
      {open && createPortal(
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
          onClick={() => setOpen(false)}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative bg-white w-full sm:w-80 sm:rounded-xl rounded-t-xl max-h-[70vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between rounded-t-xl">
              <span className="text-sm font-semibold text-gray-700">Select Platform</span>
              <button type="button" onClick={() => setOpen(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <XIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="py-1">
              {SOCIAL_PLATFORMS.map((sp) => {
                const SpIcon = PLATFORM_ICONS[sp.value] || Globe;
                const selected = sp.value === value;
                return (
                  <button
                    key={sp.value}
                    type="button"
                    onClick={() => { onChange(sp.value); setOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition ${
                      selected ? "bg-indigo-50 text-indigo-700 font-medium" : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <SpIcon className="w-5 h-5 flex-shrink-0" />
                    <span className="flex-1 text-left">{sp.label}</span>
                    {selected && <Check className="w-4 h-4 text-indigo-600" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

const PROFILE_TYPE_KEYS = [
  { value: "business", tKey: "editor.business", descKey: "editor.businessDesc" },
  { value: "professional", tKey: "editor.professional", descKey: "editor.professionalDesc" },
  { value: "personal", tKey: "editor.personal", descKey: "editor.personalDesc" },
];

const THEME_STYLE_KEYS = [
  { value: "modern", tKey: "editor.theme.modern" },
  { value: "classic", tKey: "editor.theme.classic" },
  { value: "minimal", tKey: "editor.theme.minimal" },
  { value: "bold", tKey: "editor.theme.bold" },
  { value: "pro", tKey: "editor.theme.pro" },
  { value: "instagram", tKey: "editor.theme.instagram" },
  { value: "instagram_dark", tKey: "editor.theme.instagramDark" },
];

const PRESET_COLORS = [
  "#3B82F6",
  "#2563EB",
  "#1D4ED8",
  "#EF4444",
  "#DC2626",
  "#10B981",
  "#059669",
  "#F59E0B",
  "#D97706",
  "#8B5CF6",
  "#7C3AED",
  "#EC4899",
  "#DB2777",
  "#06B6D4",
  "#0891B2",
  "#F97316",
  "#EA580C",
  "#6366F1",
  "#14B8A6",
  "#0D9488",
  "#84CC16",
  "#E11D48",
  "#000000",
  "#6B7280",
];

const MEDIA_TYPE_KEYS = [
  { value: "image", tKey: "editor.mediaType.image" },
  { value: "video", tKey: "editor.mediaType.video" },
  { value: "image_360", tKey: "editor.mediaType.image_360" },
];

/* ============================================================
   TOAST COMPONENT
   ============================================================ */

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor =
    type === "success"
      ? "bg-green-600"
      : type === "error"
      ? "bg-red-600"
      : type === "warning"
      ? "bg-amber-500"
      : "bg-gray-700";

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-lg text-white shadow-lg ${bgColor} animate-slide-up`}
    >
      {type === "success" && <Check className="w-4 h-4" />}
      {type === "warning" && <span className="w-4 h-4 text-center font-bold leading-4">!</span>}
      {type === "error" && <XIcon className="w-4 h-4" />}
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-70">
        <XIcon className="w-3 h-3" />
      </button>
    </div>
  );
}

/* ============================================================
   QR CODE MODAL
   ============================================================ */

function QRModal({ url, slug, onClose, labels, showLogo, logoUrl, fgColor }) {
  const qrRef = useRef(null);

  const handleDownloadQR = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext("2d");
    const qrImg = new window.Image();
    qrImg.onload = () => {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(qrImg, 0, 0, 512, 512);
      if (showLogo && logoUrl) {
        const logo = new window.Image();
        logo.crossOrigin = "anonymous";
        logo.onload = () => {
          const logoSize = 130;
          const x = (512 - logoSize) / 2;
          const y = (512 - logoSize) / 2;
          ctx.beginPath();
          ctx.arc(x + logoSize / 2, y + logoSize / 2, logoSize / 2 + 4, 0, Math.PI * 2);
          ctx.fillStyle = "#ffffff";
          ctx.fill();
          ctx.save();
          ctx.beginPath();
          ctx.arc(x + logoSize / 2, y + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(logo, x, y, logoSize, logoSize);
          ctx.restore();
          const link = document.createElement("a");
          link.download = `${slug}-qr.png`;
          link.href = canvas.toDataURL("image/png");
          link.click();
        };
        logo.onerror = () => {
          const link = document.createElement("a");
          link.download = `${slug}-qr.png`;
          link.href = canvas.toDataURL("image/png");
          link.click();
        };
        logo.src = logoUrl;
      } else {
        const link = document.createElement("a");
        link.download = `${slug}-qr.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
      }
    };
    qrImg.src = "data:image/svg+xml;base64," + btoa(svgData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-sm w-full mx-4">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-gray-900">{labels.qrCode}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <div ref={qrRef} className="flex justify-center mb-6 relative">
          <QRCodeSVG value={url} size={256} level="H" includeMargin fgColor={fgColor || "#000000"} />
          {showLogo && logoUrl && (
            <img
              src={logoUrl}
              alt=""
              crossOrigin="anonymous"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full border-[4px] border-white shadow-sm object-cover"
            />
          )}
        </div>
        <p className="text-xs text-center text-gray-500 mb-4 break-all">
          {url}
        </p>
        <button
          onClick={handleDownloadQR}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Download className="w-4 h-4" />
          {labels.downloadPng}
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   THEME PREVIEW COMPONENT
   ============================================================ */

function ThemePreview({ style, color, name, tagline, profileImage }) {
  const { t } = useTranslation();
  const configs = {
    modern: {
      page: "bg-gray-50 rounded-xl",
      coverGradient: `linear-gradient(135deg, ${color}, ${color}cc)`,
      coverH: "h-16",
      avatar: "rounded-full border-4 border-white shadow-lg",
      name: "text-sm font-bold text-gray-900",
      tagline: "text-xs text-gray-500",
      badge: "rounded-full text-white",
      card: "bg-white rounded-lg shadow-sm p-3",
      sectionTitle: "text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1",
      contactBg: "bg-gray-50 rounded-md",
      iconShape: "rounded-full",
    },
    classic: {
      page: "bg-gray-100 rounded-sm border border-gray-200",
      coverGradient: `linear-gradient(180deg, ${color}, ${color}55)`,
      coverH: "h-14",
      avatar: "rounded-sm border-4 border-white shadow-md",
      name: "text-sm font-bold text-gray-900 font-serif",
      tagline: "text-xs text-gray-500 italic font-serif",
      badge: "rounded-sm border border-gray-400 text-gray-600 bg-transparent",
      card: "bg-white rounded-sm border border-gray-200 p-3",
      sectionTitle: "text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-1 border-b border-gray-200 pb-1",
      contactBg: "border-b border-gray-100",
      iconShape: "rounded-sm",
    },
    minimal: {
      page: "bg-white rounded-xl border border-gray-100",
      coverGradient: `${color}11`,
      coverH: "h-8",
      avatar: "rounded-full border-2 border-gray-200",
      name: "text-sm font-light text-gray-800 tracking-wide",
      tagline: "text-xs text-gray-400 font-light",
      badge: "rounded-full border border-gray-200 text-gray-500 bg-transparent",
      card: "bg-transparent border-b border-gray-100 p-3",
      sectionTitle: "text-[10px] font-medium text-gray-300 uppercase tracking-wider mb-1",
      contactBg: "",
      iconShape: "rounded-full border border-gray-200",
    },
    bold: {
      page: "bg-gray-950 rounded-xl",
      coverGradient: `linear-gradient(160deg, ${color}, #000)`,
      coverH: "h-16",
      avatar: "rounded-xl border-4 border-gray-800 shadow-xl",
      name: "text-sm font-black text-white uppercase tracking-tight",
      tagline: "text-xs text-gray-400 font-medium",
      badge: "rounded-lg text-white",
      card: "bg-gray-900 rounded-xl border border-gray-800 p-3",
      sectionTitle: "text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1",
      contactBg: "rounded-lg bg-gray-800/50",
      iconShape: "rounded-xl",
    },
    pro: {
      page: "bg-gray-950 rounded-xl relative overflow-hidden",
      coverGradient: `linear-gradient(135deg, ${color}88, ${color}22)`,
      coverH: "h-14",
      avatar: "rounded-2xl border-2 border-white/20 shadow-2xl",
      name: "text-sm font-bold text-white",
      tagline: "text-xs text-gray-400",
      badge: "rounded-full text-white/70 bg-white/10",
      card: "bg-white/5 backdrop-blur-md rounded-xl border border-white/10 p-3",
      sectionTitle: "text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1",
      contactBg: "rounded-lg bg-white/5",
      iconShape: "rounded-xl",
      meshBlob: true,
    },
    instagram: {
      page: "bg-white rounded-xl border border-gray-200",
      coverGradient: "transparent",
      coverH: "h-0",
      avatar: "rounded-full",
      name: "text-xs font-semibold text-gray-900",
      tagline: "text-xs text-gray-500",
      badge: "rounded text-gray-500 bg-gray-100",
      card: "bg-white border-b border-gray-200 p-3",
      sectionTitle: "text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1",
      contactBg: "rounded-md bg-gray-50",
      iconShape: "rounded-full",
      igRing: true,
      noOverlap: true,
    },
    instagram_dark: {
      page: "bg-gray-950 rounded-xl border border-gray-800",
      coverGradient: "transparent",
      coverH: "h-0",
      avatar: "rounded-full",
      name: "text-xs font-semibold text-white",
      tagline: "text-xs text-gray-300",
      badge: "rounded text-gray-300 bg-gray-800",
      card: "bg-gray-950 border-b border-gray-800 p-3",
      sectionTitle: "text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1",
      contactBg: "rounded-md bg-gray-900",
      iconShape: "rounded-full",
      igRing: true,
      igDark: true,
      noOverlap: true,
    },
  };
  const c = configs[style] || configs.modern;
  const isDark = ["bold", "pro", "instagram_dark"].includes(style);

  const isIg = style === "instagram" || style === "instagram_dark";
  const isPro = style === "pro";

  return (
    <div className={`overflow-hidden ${c.page}`}>
      {/* Pro gradient mesh blobs */}
      {c.meshBlob && (
        <>
          <div className="absolute top-0 left-0 w-1/2 h-1/2 rounded-full opacity-20 blur-[40px]" style={{ background: color }} />
          <div className="absolute bottom-0 right-0 w-1/3 h-1/3 rounded-full opacity-15 blur-[30px]" style={{ background: color }} />
        </>
      )}
      {/* Cover */}
      {c.coverH !== "h-0" && (
        <div className={`w-full ${c.coverH}`} style={{ background: c.coverGradient }} />
      )}
      <div className={`px-4 pb-4 relative ${c.noOverlap || c.coverH === "h-0" ? "pt-4" : "-mt-5"}`}>
        {/* Instagram horizontal header */}
        {isIg ? (
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-shrink-0" style={{ background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)", padding: "2px", borderRadius: "9999px" }}>
              <div className={`w-10 h-10 overflow-hidden ${c.avatar} border-2 ${c.igDark ? "border-gray-950" : "border-white"}`}>
                {profileImage ? (
                  <img src={profileImage} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: color }}>
                    {name.charAt(0)?.toUpperCase()}
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className={c.name}>{name}</div>
              <div className={c.tagline}>{tagline}</div>
            </div>
          </div>
        ) : isPro ? (
          /* Pro horizontal header */
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 overflow-hidden flex-shrink-0 ${c.avatar}`}>
              {profileImage ? (
                <img src={profileImage} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: color }}>
                  {name.charAt(0)?.toUpperCase()}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className={c.name}>{name}</div>
              <div className={c.tagline}>{tagline}</div>
            </div>
          </div>
        ) : (
          /* Default centered header */
          <>
            <div className="flex justify-center">
              <div className={`w-10 h-10 overflow-hidden ${c.avatar}`}>
                {profileImage ? (
                  <img src={profileImage} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: color }}>
                    {name.charAt(0)?.toUpperCase()}
                  </div>
                )}
              </div>
            </div>
            <div className="text-center mt-2">
              <div className={c.name}>{name}</div>
              <div className={c.tagline}>{tagline}</div>
              <span className={`inline-block mt-1 px-2 py-0.5 text-[9px] font-medium ${c.badge}`} style={c.badge.includes("bg-transparent") || c.badge.includes("bg-white") || c.badge.includes("bg-gray") ? {} : { backgroundColor: color }}>
                {t("editor.preview.badge")}
              </span>
            </div>
          </>
        )}
        <div className={`${isIg || isPro ? "" : "mt-3"} space-y-2`}>
          <div className={c.card}>
            <div className={c.sectionTitle}>{t("editor.preview.contact")}</div>
            <div className="space-y-1">
              {["email@example.com", "+1 234 5678"].map((val, i) => (
                <div key={i} className={`flex items-center gap-2 py-1 px-1 ${c.contactBg}`}>
                  <div className={`w-5 h-5 ${c.iconShape} flex items-center justify-center flex-shrink-0`} style={{ backgroundColor: c.iconShape.includes("border") ? "transparent" : color, borderColor: color }}>
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c.iconShape.includes("border") ? color : "white" }} />
                  </div>
                  <span className={`text-[10px] ${isDark ? "text-gray-300" : "text-gray-600"}`}>{val}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Instagram gallery grid preview */}
          {isIg ? (
            <div className="grid grid-cols-3 gap-[1px]">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className={`aspect-square ${c.igDark ? "bg-gray-800" : "bg-gray-100"}`} />
              ))}
            </div>
          ) : (
            <div className={c.card}>
              <div className={c.sectionTitle}>{t("editor.preview.social")}</div>
              <div className="flex gap-1.5">
                {[1, 2, 3].map((i) => (
                  <div key={i} className={`w-6 h-6 ${c.iconShape} flex items-center justify-center`} style={{ backgroundColor: c.iconShape.includes("border") ? "transparent" : color, borderColor: color }}>
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c.iconShape.includes("border") ? color : "white" }} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   IMAGE CROP HELPERS
   ============================================================ */

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = url;
  });
}

// Shrink a photo to at most 1600px and re-encode it as JPEG before upload.
// GIFs are left alone (re-encoding would drop the animation); on any error the
// original file is returned.
const UPLOAD_IMAGE_MAX = 1600;
function compressImage(file) {
  if (!file.type?.startsWith("image/") || file.type === "image/gif") return Promise.resolve(file);
  return new Promise((resolve) => {
    const img = new window.Image();
    const src = URL.createObjectURL(file);
    img.onload = () => {
      let w = img.width, h = img.height;
      if (w > UPLOAD_IMAGE_MAX || h > UPLOAD_IMAGE_MAX) {
        const scale = UPLOAD_IMAGE_MAX / Math.max(w, h);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(src);
      canvas.toBlob((b) => resolve(b || file), "image/jpeg", 0.88);
    };
    img.onerror = () => {
      URL.revokeObjectURL(src);
      resolve(file);
    };
    img.src = src;
  });
}

async function getCroppedBlob(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);

  // Cap dimensions — no profile/cover image needs to be larger than 1600px
  const MAX = 1600;
  let outW = pixelCrop.width;
  let outH = pixelCrop.height;
  if (outW > MAX || outH > MAX) {
    const scale = MAX / Math.max(outW, outH);
    outW = Math.round(outW * scale);
    outH = Math.round(outH * scale);
  }

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outW,
    outH
  );
  return new Promise((resolve) =>
    canvas.toBlob((blob) => {
      console.log(`[image-compress] ${pixelCrop.width}x${pixelCrop.height} → ${outW}x${outH}, size: ${(blob.size / 1024).toFixed(0)}KB`);
      resolve(blob);
    }, "image/jpeg", 0.92)
  );
}

function ImageCropModal({ imageSrc, aspect, onConfirm, onCancel, labels }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);
    onConfirm(blob);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="relative h-80 bg-gray-900">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape={aspect === 1 ? "round" : "rect"}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="px-5 py-3">
          <label className="flex items-center gap-3 text-sm text-gray-500">
            {labels.zoom}
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-blue-600"
            />
          </label>
        </div>
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            {labels.cancel}
          </button>
          <button
            onClick={handleConfirm}
            className="px-5 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition font-medium"
          >
            {labels.cropUpload}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   SORTABLE BLOCK WRAPPER
   ============================================================ */

function SortableBlock({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
    zIndex: isDragging ? 50 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      data-block-id={id}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden"
    >
      {children({ ...listeners })}
    </div>
  );
}

function SortableMediaItem({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : "auto",
    touchAction: "none",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ dragListeners: listeners })}
    </div>
  );
}

/* ============================================================
   CUSTOM TEXT EDITOR (own state to avoid race conditions)
   ============================================================ */

function CustomTextEditor({ block, config, updateBlockConfig, handleUpdateBlock, t }) {
  const [content, setContent] = useState(config.content || "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = (val) => {
    setContent(val);
    setDirty(true);
    updateBlockConfig(block.id, { content: val });
  };

  const handleSave = async () => {
    setSaving(true);
    await handleUpdateBlock(block.id, {
      config: { ...config, content },
    });
    setDirty(false);
    setSaving(false);
  };

  const handleToggleTitle = async (checked) => {
    updateBlockConfig(block.id, { show_title: checked });
    await handleUpdateBlock(block.id, {
      config: { ...config, content, show_title: checked },
    });
  };

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={config.show_title !== false}
          onChange={(e) => handleToggleTitle(e.target.checked)}
          className="rounded border-gray-300 text-indigo-600"
        />
        {t("editor.showTitle")}
      </label>
      <ReactQuill
        theme="snow"
        value={content}
        onChange={handleChange}
        modules={{
          toolbar: [
            [{ header: [2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "blockquote"],
            ["clean"],
          ],
        }}
        placeholder={t("editor.placeholder.content")}
      />
      {dirty && (
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition disabled:opacity-50"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
          ) : (
            <Save className="h-3.5 w-3.5" />
          )}
          {t("editor.save")}
        </button>
      )}
    </div>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

export default function ProfileEditorPage() {
  const { slug: id } = useParams();
  const navigate = useNavigate();
  const { socialBaseUrls, freeBlockedBlocks, tiers, blockTiers, featureTiers, loginLogo, appName, maxUploadMb } = useAppConfig();
  const { t } = useTranslation();

  // Translated constant arrays
  const TABS = TAB_KEYS.map((tab) => ({ ...tab, label: t(tab.tKey) }));
  const BLOCK_TYPES = BLOCK_TYPE_KEYS.map((bt) => ({ ...bt, label: t(bt.tKey) }));
  const PROFILE_TYPES = PROFILE_TYPE_KEYS.map((pt) => ({ ...pt, label: t(pt.tKey), desc: t(pt.descKey) }));
  const THEME_STYLES = THEME_STYLE_KEYS.map((ts) => ({ ...ts, label: t(ts.tKey) }));
  const MEDIA_TYPES = MEDIA_TYPE_KEYS.map((mt) => ({ ...mt, label: t(mt.tKey) }));

  /* ----------------------------------------------------------
     STATE
     ---------------------------------------------------------- */

  // UI state
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab = TAB_KEYS.some((t) => t.key === tabParam) ? tabParam : "general";
  const setActiveTab = (tab) => {
    setSearchParams({ tab }, { replace: true });
    window.scrollTo({ top: 0 });
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const confirmRef = useRef(null);
  const showConfirm = (message, onConfirm) => {
    confirmRef.current = onConfirm;
    setConfirmModal(message);
  };
  const [shareDropdownOpen, setShareDropdownOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [addBlockDropdownOpen, setAddBlockDropdownOpen] = useState(false);
  const [resetModal, setResetModal] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [slugChangeModal, setSlugChangeModal] = useState(false);
  const blockParam = searchParams.get("block");
  const [expandedBlocks, setExpandedBlocks] = useState(() => {
    if (blockParam) return { [parseInt(blockParam)]: true };
    return {};
  });
  const [expandedGalleries, setExpandedGalleries] = useState({});
  const [newGalleryTitle, setNewGalleryTitle] = useState("");
  const [showNewGalleryInput, setShowNewGalleryInput] = useState(false);
  const [editingMediaItem, setEditingMediaItem] = useState(null); // {galleryId, item}
  const [urlCopied, setUrlCopied] = useState(false);
  const [linkedCards, setLinkedCards] = useState([]);
  const [cardCode, setCardCode] = useState("");
  const [cardLinking, setCardLinking] = useState(false);
  const [cardError, setCardError] = useState("");
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanMode, setScanMode] = useState("nfc"); // "nfc" or "qr"
  const [qrScanning, setQrScanning] = useState(false);
  const [qrError, setQrError] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const [cropModal, setCropModal] = useState(null); // { src, type: "profile"|"cover" }
  const [selectedRef, setSelectedRef] = useState(""); // referrer tracking source

  // Stats state

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewsData, setReviewsData] = useState({ total: 0, avg_rating: 0, rating_counts: {} });
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewPeriod, setReviewPeriod] = useState("all");
  const [reviewRating, setReviewRating] = useState(0);

  // CRM state
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsFilter, setLeadsFilter] = useState("");
  const [editingNotes, setEditingNotes] = useState({}); // { leadId: notesText }
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  // Profile data
  const [profile, setProfile] = useState({
    name: "",
    tagline: "",
    description: "",
    slug: "",
    profile_type: "professional",
    is_active: true,
    theme_color: "#3B82F6",
    theme_style: "modern",
    profile_image_url: null,
    cover_image_url: null,
    view_count: 0,
    settings: {},
  });

  const [originalSlug, setOriginalSlug] = useState("");

  // Build tier lookup helpers
  const tiersByPosition = Object.fromEntries(tiers.map((t) => [t.position, t]));
  const profileTierObj = tiers.find((t) => t.name === profile.tier) || tiers[0];
  const profileTierPos = profileTierObj?.position ?? 0;

  const getBlockTierBadge = (blockType) => {
    const minPos = blockTiers[blockType] ?? 0;
    if (minPos <= profileTierPos) return null;
    const tier = tiersByPosition[minPos];
    if (!tier) return null;
    return { label: tier.label, color: tier.color };
  };

  // Blocks and galleries
  const [blocks, setBlocks] = useState([]);
  const [galleries, setGalleries] = useState([]);

  // Storage
  const [storage, setStorage] = useState(null); // { used_mb, max_mb }

  // Upload progress
  const [uploadProgress, setUploadProgress] = useState(null); // { type, progress: 0-100 }
  const uploadAbortRef = useRef(null);

  // Image source picker (camera vs gallery)
  const [imageSourcePicker, setImageSourcePicker] = useState(null); // "profile" | "cover" | "pwa_icon" | null
  const cameraInputRef = useRef(null);

  // Refs for file inputs
  const profileImageRef = useRef(null);
  const coverImageRef = useRef(null);
  const pwaIconRef = useRef(null);
  const welcomeScreenRef = useRef(null);
  const galleryUploadRefs = useRef({});
  const galleryCameraRefs = useRef({});
  const tabNavRef = useRef(null);
  const [tabScrollState, setTabScrollState] = useState({ left: false, right: false });

  const updateTabScroll = useCallback(() => {
    const nav = tabNavRef.current;
    if (!nav) return;
    setTabScrollState({
      left: nav.scrollLeft > 5,
      right: nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 5,
    });
  }, []);

  useEffect(() => {
    // Delay initial check so the nav has rendered at full width
    const timer = setTimeout(updateTabScroll, 100);
    window.addEventListener("resize", updateTabScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateTabScroll);
    };
  }, [updateTabScroll, activeTab]);

  const baseUrl = window.location.origin;
  const profileUrl = `${baseUrl}/p/${profile.slug}`;
  const trackedUrl = selectedRef ? `${profileUrl}?ref=${selectedRef}` : profileUrl;

  /* ----------------------------------------------------------
     DATA FETCHING
     ---------------------------------------------------------- */

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await getProfile(id);

      // If slug changed, redirect to new URL
      if (res.data.redirect_slug) {
        navigate(`/app/profile/${res.data.redirect_slug}`, { replace: true });
        return;
      }

      const p = res.data.profile;
      setProfile({
        name: p.profile_name || "",
        tagline: p.tagline || "",
        description: p.description || "",
        slug: p.slug || "",
        profile_type: p.profile_type || "professional",
        is_active: p.is_active !== false,
        theme_color: p.theme_color || "#3B82F6",
        theme_style: p.theme_style || "modern",
        profile_image_url: p.profile_image || null,
        cover_image_url: p.cover_image || null,
        view_count: p.views_count || 0,
        settings: p.settings || {},
        organization_id: p.organization_id || null,
        job_title: p.job_title || "",
        tier: p.tier || "free",
      });
      setOriginalSlug(p.slug || "");
      if (p.blocks) {
        setBlocks(p.blocks);
      }
    } catch (err) {
      showToast(t("editor.toast.profileSaveFailed"), "error");
    }
  }, [id, showToast]);

  const fetchBlocks = useCallback(async () => {
    try {
      const res = await getBlocks(id);
      setBlocks(res.data.blocks || []);
    } catch (err) {
      // blocks may have been loaded with the profile
    }
  }, [id]);

  const fetchGalleries = useCallback(async () => {
    try {
      const res = await getGalleries(id);
      setGalleries(res.data.galleries || []);
    } catch (err) {
      showToast(t("editor.toast.galleryCreateFailed"), "error");
    }
  }, [id, showToast]);

  const fetchStorage = useCallback(async () => {
    try {
      const res = await getProfileStorage(id);
      setStorage(res.data);
    } catch { /* ignore */ }
  }, [id]);

  const fetchCards = useCallback(async () => {
    try {
      const res = await getProfileCards(id);
      setLinkedCards(res.data.cards || []);
    } catch (err) {
      // ignore — cards are optional
    }
  }, [id]);

  const handleLinkCard = async (e) => {
    e.preventDefault();
    const code = cardCode.trim().toUpperCase();
    if (!code) return;
    setCardLinking(true);
    setCardError("");
    try {
      const res = await linkCard(code, parseInt(id));
      setLinkedCards((prev) => [res.data.card, ...prev]);
      setCardCode("");
      showToast(t("editor.toast.cardLinked"));
    } catch (err) {
      setCardError(err.response?.data?.error || t("editor.toast.cardLinkFailed"));
    } finally {
      setCardLinking(false);
    }
  };

  const stopQrScanner = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setQrScanning(false);
  }, []);

  const startQrScanner = useCallback(async () => {
    setQrError("");
    if (!("BarcodeDetector" in window)) {
      setQrError(t("editor.toast.qrNotSupported"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setQrScanning(true);
      const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
      scanIntervalRef.current = setInterval(async () => {
        if (!videoRef.current || videoRef.current.readyState < 2) return;
        try {
          const barcodes = await detector.detect(videoRef.current);
          if (barcodes.length > 0) {
            const url = barcodes[0].rawValue;
            // Extract /c/CODE from the URL
            const match = url.match(/\/c\/([A-Za-z0-9_-]+)/);
            if (match) {
              stopQrScanner();
              window.location.href = url;
            }
          }
        } catch (e) {
          // scan frame error, ignore
        }
      }, 300);
    } catch (err) {
      setQrError(t("editor.toast.cameraFailed"));
    }
  }, [stopQrScanner]);

  const openScanModal = () => {
    localStorage.setItem("pending_link_profile_id", id);
    setScanMode("nfc");
    setQrError("");
    setShowScanModal(true);
  };

  const closeScanModal = () => {
    stopQrScanner();
    localStorage.removeItem("pending_link_profile_id");
    setShowScanModal(false);
    fetchCards();
  };

  const handleUnlinkCard = async (code) => {
    try {
      await unlinkCardUser(code);
      setLinkedCards((prev) => prev.filter((c) => c.code !== code));
      showToast(t("editor.toast.cardUnlinked"));
    } catch (err) {
      showToast(t("editor.toast.cardUnlinkFailed"), "error");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchProfile(), fetchBlocks(), fetchGalleries(), fetchCards(), fetchStorage()]);
      setLoading(false);
    };
    loadData();
  }, [fetchProfile, fetchBlocks, fetchGalleries, fetchCards, fetchStorage]);

  // Scroll to target block from URL param
  useEffect(() => {
    if (blockParam && !loading && blocks.length > 0) {
      const bid = parseInt(blockParam);
      setExpandedBlocks((prev) => ({ ...prev, [bid]: true }));
      setTimeout(() => {
        const el = document.querySelector(`[data-block-id="${bid}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [blockParam, loading, blocks.length]); // eslint-disable-line

  // Highlight upload area from sessionStorage (auto-click blocked on mobile)
  const [highlightUpload, setHighlightUpload] = useState(null);
  useEffect(() => {
    if (loading || activeTab !== "appearance") return;
    const pending = sessionStorage.getItem("pending_upload");
    if (!pending) return;
    sessionStorage.removeItem("pending_upload");
    setHighlightUpload(pending);
    const timer = setTimeout(() => {
      const el = document.getElementById(`upload-${pending}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    const clear = setTimeout(() => setHighlightUpload(null), 3000);
    return () => { clearTimeout(timer); clearTimeout(clear); };
  }, [loading, activeTab]);

  // Load CRM leads when tab is active
  const fetchLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const params = leadsFilter ? { status: leadsFilter } : {};
      const res = await getLeads(id, params);
      setLeads(res.data.leads || []);
    } catch {
      setLeads([]);
    }
    setLeadsLoading(false);
  }, [id, leadsFilter]);

  useEffect(() => {
    if (activeTab === "crm") {
      fetchLeads();
      // Check push subscription status
      getPushSubscription().then((sub) => setPushEnabled(!!sub));
    }
  }, [activeTab, fetchLeads]);

  // Fetch reviews when reviews tab is active or filters change
  useEffect(() => {
    if (activeTab !== "reviews") return;
    setReviewsLoading(true);
    const params = { period: reviewPeriod };
    if (reviewRating) params.rating = reviewRating;
    getReviews(id, params)
      .then((res) => {
        setReviews(res.data.reviews || []);
        setReviewsData({
          total: res.data.total || 0,
          avg_rating: res.data.avg_rating || 0,
          rating_counts: res.data.rating_counts || {},
        });
      })
      .catch(() => {})
      .finally(() => setReviewsLoading(false));
  }, [activeTab, id, reviewPeriod, reviewRating]);


  const handleTogglePush = async () => {
    setPushLoading(true);
    try {
      if (pushEnabled) {
        const sub = await getPushSubscription();
        if (sub) {
          await unsubscribePush(sub.endpoint);
          await unsubscribeFromPush();
        }
        setPushEnabled(false);
      } else {
        const sub = await subscribeToPush();
        if (sub) {
          await subscribePush(sub);
          setPushEnabled(true);
        }
      }
    } catch {
      // ignore
    }
    setPushLoading(false);
  };

  const handleUpdateLeadStatus = async (leadId, status) => {
    try {
      await updateLead(id, leadId, { status });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status } : l)));
    } catch {
      showToast(t("contactForm.error"), "error");
    }
  };

  const handleSaveLeadNotes = async (leadId) => {
    const notes = editingNotes[leadId];
    if (notes === undefined) return;
    try {
      await updateLead(id, leadId, { notes });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, notes } : l)));
      setEditingNotes((prev) => {
        const copy = { ...prev };
        delete copy[leadId];
        return copy;
      });
    } catch {
      showToast(t("contactForm.error"), "error");
    }
  };

  const handleDeleteLead = (leadId) => {
    showConfirm(t("crm.deleteConfirm"), async () => {
      try {
        await deleteLeadApi(id, leadId);
        setLeads((prev) => prev.filter((l) => l.id !== leadId));
      } catch {
        showToast(t("contactForm.error"), "error");
      }
    });
  };

  const exportLeadsCsv = () => {
    const headers = ["Name", "Email", "WhatsApp", "Message", "Status", "Source", "Date"];
    const rows = leads.map((l) => [
      l.name, l.email || "", l.whatsapp || "", (l.message || "").replace(/\n/g, " "),
      l.status, l.source_ref || "direct", l.created_at ? new Date(l.created_at).toLocaleString() : "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${profile.slug || id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ----------------------------------------------------------
     SAVE PROFILE
     ---------------------------------------------------------- */

  const handleSave = async () => {
    setSlugChangeModal(false);
    setSaving(true);
    try {
      const res = await updateProfile(id, {
        profile_name: profile.name,
        job_title: profile.job_title,
        tagline: profile.tagline,
        description: profile.description,
        slug: profile.slug,
        profile_type: profile.profile_type,
        is_active: profile.is_active,
        theme_color: profile.theme_color,
        theme_style: profile.theme_style,
        settings: profile.settings,
      });
      showToast(t("editor.toast.profileSaved"));

      // If slug changed, update URL
      const newSlug = res.data.profile?.slug;
      if (newSlug && newSlug !== id) {
        navigate(`/app/profile/${newSlug}?tab=${activeTab}`, { replace: true });
        return;
      }

      // Check for empty contact/social fields after save
      let firstEmpty = null;
      for (const b of blocks) {
        if (b.block_type === "contact_info") {
          for (const item of (b.contact_items || b.contacts || [])) {
            if (!item.value || !item.value.trim()) {
              firstEmpty = { type: "contact", id: item.id };
              break;
            }
          }
        }
        if (!firstEmpty && b.block_type === "social_links") {
          for (const link of (b.social_links || [])) {
            if (!link.url || !link.url.trim()) {
              firstEmpty = { type: "social", id: link.id };
              break;
            }
          }
        }
        if (firstEmpty) break;
      }

      if (firstEmpty) {
        showToast(t("editor.toast.emptyFields"), "warning");
        setTimeout(() => {
          const attr = firstEmpty.type === "contact" ? "data-contact-id" : "data-social-id";
          const el = document.querySelector(`[${attr}="${firstEmpty.id}"]`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.classList.add("ring-2", "ring-amber-400");
            setTimeout(() => el.classList.remove("ring-2", "ring-amber-400"), 2500);
          }
        }, 300);
      }
    } catch (err) {
      showToast(
        err.response?.data?.error || t("editor.toast.profileSaveFailed"),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  /* ----------------------------------------------------------
     IMAGE UPLOADS
     ---------------------------------------------------------- */

  const handleImageSelect = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const reader = new FileReader();
    reader.onload = () => setCropModal({ src: reader.result, type });
    reader.readAsDataURL(file);
  };

  const cancelUpload = () => {
    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort();
      uploadAbortRef.current = null;
    }
    setUploadProgress(null);
  };

  const handleCroppedUpload = async (blob) => {
    const type = cropModal.type;
    setCropModal(null);
    const formData = new FormData();
    formData.append("file", blob, `${type}.jpg`);
    formData.append("type", type);

    const abortController = new AbortController();
    uploadAbortRef.current = abortController;
    setUploadProgress({ type, progress: 0, fileSize: blob.size, loaded: 0 });

    try {
      const res = await uploadProfileImage(id, formData, {
        onUploadProgress: (e) => {
          if (e.total) {
            setUploadProgress((prev) => prev ? { ...prev, progress: Math.round((e.loaded * 100) / e.total), loaded: e.loaded, fileSize: e.total } : prev);
          }
        },
        signal: abortController.signal,
      });
      if (type === "pwa_icon") {
        setProfile((prev) => ({
          ...prev,
          settings: { ...(prev.settings || {}), pwa_icon: res.data.url },
        }));
        showToast(t("editor.toast.pwaIconUploaded"));
      } else {
        const key = type === "profile" ? "profile_image_url" : "cover_image_url";
        setProfile((prev) => ({ ...prev, [key]: res.data.url }));
        showToast(type === "profile" ? t("editor.toast.profileImageUploaded") : t("editor.toast.coverImageUploaded"));
      }
      fetchStorage();
    } catch (err) {
      if (abortController.signal.aborted) {
        showToast(t("editor.toast.uploadCancelled"), "error");
      } else {
        showToast(t("editor.toast.uploadFailed"), "error");
      }
    } finally {
      setUploadProgress(null);
      uploadAbortRef.current = null;
    }
  };

  const handleWelcomeScreenUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "welcome_screen");

    const abortController = new AbortController();
    uploadAbortRef.current = abortController;
    setUploadProgress({ type: "welcome_screen", progress: 0, fileSize: file.size, loaded: 0 });

    try {
      const res = await uploadProfileImage(id, formData, {
        onUploadProgress: (ev) => {
          if (ev.total) setUploadProgress((prev) => prev ? { ...prev, type: "welcome_screen", progress: Math.round((ev.loaded * 100) / ev.total), loaded: ev.loaded, fileSize: ev.total } : prev);
        },
        signal: abortController.signal,
      });
      setProfile((prev) => ({
        ...prev,
        settings: {
          ...(prev.settings || {}),
          welcome_screen: {
            ...((prev.settings || {}).welcome_screen || {}),
            media_url: res.data.url,
            media_type: res.data.media_type,
          },
        },
      }));
      showToast(t("editor.toast.profileSaved"));
      fetchStorage();
    } catch (err) {
      if (!abortController.signal.aborted) showToast(t("editor.toast.uploadFailed"), "error");
    } finally {
      setUploadProgress(null);
      uploadAbortRef.current = null;
    }
  };

  /* ----------------------------------------------------------
     SHARE ACTIONS
     ---------------------------------------------------------- */

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(trackedUrl);
      setUrlCopied(true);
      showToast(t("editor.toast.urlCopied"));
      setTimeout(() => setUrlCopied(false), 2000);
    } catch {
      showToast(t("editor.toast.urlCopyFailed"), "error");
    }
  };

  const handleDownloadVCard = () => {
    window.open(`/api/share/vcard/${profile.slug}`, "_blank");
  };

  /* ----------------------------------------------------------
     BLOCK MANAGEMENT
     ---------------------------------------------------------- */

  const handleAddBlock = async (blockType) => {
    setAddBlockDropdownOpen(false);
    const label = BLOCK_TYPES.find((b) => b.value === blockType)?.label || blockType;
    const titleKey = BLOCK_DEFAULT_TITLE_KEY[blockType];
    const titleI18n = titleKey
      ? { en: translations.en[titleKey] || "", es: translations.es[titleKey] || "" }
      : {};
    const extraConfig = {};
    if (blockType === "payment") {
      extraConfig.description_i18n = {
        en: translations.en["editor.payment.descriptionDefault.en"] || "",
        es: translations.es["editor.payment.descriptionDefault.es"] || "",
      };
    }
    try {
      const res = await createBlock(id, {
        block_type: blockType,
        title: "",
        config: { title_i18n: titleI18n, ...extraConfig },
        is_visible: true,
        sort_order: blocks.length,
      });
      const newBlock = res.data.block;
      setBlocks((prev) => [...prev, newBlock]);
      setExpandedBlocks((prev) => ({ ...prev, [newBlock.id]: true }));
      showToast(t("editor.toast.blockAdded").replace("{label}", label));
    } catch (err) {
      if (err.response?.data?.error === "tier_required") {
        showToast(t("editor.toast.tierRequired") || "This feature requires a Plus subscription", "error");
      } else {
        showToast(t("editor.toast.blockAddFailed"), "error");
      }
    }
  };

  const handleUpdateBlock = async (blockId, updates) => {
    try {
      await updateBlock(blockId, updates);
      setBlocks((prev) =>
        prev.map((b) => (b.id === blockId ? { ...b, ...updates } : b))
      );
    } catch (err) {
      showToast(t("editor.toast.blockUpdateFailed"), "error");
    }
  };

  const handleDeleteBlock = (blockId) => {
    showConfirm(t("editor.deleteBlock"), async () => {
      try {
        await deleteBlock(blockId);
        setBlocks((prev) => prev.filter((b) => b.id !== blockId));
        showToast(t("editor.toast.blockDeleted"));
      } catch (err) {
        showToast(t("editor.toast.blockDeleteFailed"), "error");
      }
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    const reordered = arrayMove(blocks, oldIndex, newIndex).map((b, i) => ({
      ...b,
      sort_order: i,
    }));
    setBlocks(reordered);
    try {
      await reorderBlocks(id, reordered.map((b) => b.id));
    } catch (err) {
      showToast(t("editor.toast.reorderFailed"), "error");
    }
  };

  const toggleBlockExpanded = (blockId) => {
    setExpandedBlocks((prev) => ({ ...prev, [blockId]: !prev[blockId] }));
  };

  /* ----------------------------------------------------------
     CONTACT ITEMS (within contact_info blocks)
     ---------------------------------------------------------- */

  const handleAddContact = async (blockId) => {
    try {
      const res = await addContactItem(blockId, {
        contact_type: "email",
        label: "",
        value: "",
      });
      setBlocks((prev) =>
        prev.map((b) => {
          if (b.id !== blockId) return b;
          const contacts = b.contact_items || [];
          const newItem = res.data.contact_item;
          return { ...b, contact_items: [...contacts, newItem] };
        })
      );
    } catch (err) {
      showToast(t("editor.toast.contactAddFailed"), "error");
    }
  };

  const handleUpdateContact = async (itemId, updates) => {
    try {
      await updateContactItem(itemId, updates);
      setBlocks((prev) =>
        prev.map((b) => {
          const contacts = b.contact_items || [];
          const hasItem = contacts.some((c) => c.id === itemId);
          if (!hasItem) return b;
          const updated = contacts.map((c) =>
            c.id === itemId ? { ...c, ...updates } : c
          );
          return { ...b, contact_items: updated };
        })
      );
    } catch (err) {
      showToast(t("editor.toast.contactUpdateFailed"), "error");
    }
  };

  const handleDeleteContact = async (itemId) => {
    try {
      await deleteContactItem(itemId);
      setBlocks((prev) =>
        prev.map((b) => {
          const contacts = b.contact_items || [];
          const filtered = contacts.filter((c) => c.id !== itemId);
          return { ...b, contact_items: filtered, contacts: filtered };
        })
      );
    } catch (err) {
      showToast(t("editor.toast.contactDeleteFailed"), "error");
    }
  };

  /* ----------------------------------------------------------
     SOCIAL LINKS (within social_links blocks)
     ---------------------------------------------------------- */

  const handleAddSocial = async (blockId) => {
    try {
      const res = await addSocialLink(blockId, {
        platform: "instagram",
        url: "",
      });
      setBlocks((prev) =>
        prev.map((b) => {
          if (b.id !== blockId) return b;
          const links = b.social_links || [];
          const newLink = res.data.social_link;
          return { ...b, social_links: [...links, newLink] };
        })
      );
    } catch (err) {
      showToast(t("editor.toast.socialAddFailed"), "error");
    }
  };

  const handleUpdateSocial = async (linkId, updates) => {
    try {
      await updateSocialLink(linkId, updates);
      setBlocks((prev) =>
        prev.map((b) => {
          const links = b.social_links || [];
          const hasLink = links.some((l) => l.id === linkId);
          if (!hasLink) return b;
          return {
            ...b,
            social_links: links.map((l) =>
              l.id === linkId ? { ...l, ...updates } : l
            ),
          };
        })
      );
    } catch (err) {
      showToast(t("editor.toast.socialUpdateFailed"), "error");
    }
  };

  const handleDeleteSocial = async (linkId) => {
    try {
      await deleteSocialLink(linkId);
      setBlocks((prev) =>
        prev.map((b) => ({
          ...b,
          social_links: (b.social_links || []).filter((l) => l.id !== linkId),
        }))
      );
    } catch (err) {
      showToast(t("editor.toast.socialDeleteFailed"), "error");
    }
  };

  /* ----------------------------------------------------------
     GALLERY MANAGEMENT
     ---------------------------------------------------------- */

  const handleCreateGallery = async () => {
    if (!newGalleryTitle.trim()) return;
    try {
      const res = await createGallery(id, { title: newGalleryTitle.trim() });
      const newGallery = res.data.gallery;
      setGalleries((prev) => [...prev, newGallery]);
      setNewGalleryTitle("");
      setShowNewGalleryInput(false);
      setExpandedGalleries((prev) => ({ ...prev, [newGallery.id]: true }));
      showToast(t("editor.toast.galleryCreated"));
    } catch (err) {
      showToast(t("editor.toast.galleryCreateFailed"), "error");
    }
  };

  const handleUpdateGallery = async (galleryId, updates) => {
    try {
      await updateGallery(galleryId, updates);
      setGalleries((prev) =>
        prev.map((g) => (g.id === galleryId ? { ...g, ...updates } : g))
      );
    } catch (err) {
      showToast(t("editor.toast.galleryUpdateFailed"), "error");
    }
  };

  const handleDeleteGallery = (galleryId) => {
    showConfirm(t("editor.deleteGallery"), async () => {
      try {
        await deleteGallery(galleryId);
        setGalleries((prev) => prev.filter((g) => g.id !== galleryId));
        showToast(t("editor.toast.galleryDeleted"));
      } catch (err) {
        showToast(t("editor.toast.galleryDeleteFailed"), "error");
      }
    });
  };

  // Videos are sent as-is, so reject oversized ones before uploading instead
  // of leaving the upload stuck (the server would refuse them anyway)
  const videoFitsLimits = (file) => {
    const sizeMb = Math.ceil(file.size / (1024 * 1024));
    if (sizeMb > maxUploadMb) {
      showToast(t("editor.fileTooBig", { size: sizeMb, max: maxUploadMb }), "error");
      return false;
    }
    if (storage && storage.used_mb + sizeMb > storage.max_mb) {
      const free = Math.max(0, Math.floor(storage.max_mb - storage.used_mb));
      showToast(t("editor.notEnoughStorage", { size: sizeMb, free, max: storage.max_mb }), "error");
      return false;
    }
    return true;
  };

  // Shared by gallery and media block uploads: size check, photo compression,
  // progress overlay with cancel, and the same messages. Returns the response or null.
  const uploadWithProgress = async (file, isVideo, fallbackName, send) => {
    if (isVideo && !videoFitsLimits(file)) return null;
    const uploadFile = isVideo ? file : await compressImage(file);
    const abortController = new AbortController();
    uploadAbortRef.current = abortController;
    setUploadProgress({ type: "media", progress: 0, fileName: file.name, fileSize: uploadFile.size || file.size, loaded: 0 });
    const formData = new FormData();
    formData.append("file", uploadFile, uploadFile === file ? file.name : fallbackName);
    try {
      const res = await send(formData, {
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress((prev) => prev ? { ...prev, progress: Math.round((e.loaded / e.total) * 100), loaded: e.loaded, fileSize: e.total } : prev);
        },
        signal: abortController.signal,
      });
      showToast(t("editor.toast.mediaUploaded"));
      fetchStorage();
      return res;
    } catch (err) {
      if (err?.code === "ERR_CANCELED") { /* user cancelled */ }
      else if (err?.response?.status === 413) showToast(t("editor.storageFull"), "error");
      else if (!err?.response) showToast(t("editor.toast.uploadInterrupted", { max: maxUploadMb }), "error");
      else showToast(t("editor.toast.mediaUploadFailed"), "error");
      return null;
    } finally {
      setUploadProgress(null);
      uploadAbortRef.current = null;
    }
  };

  const handleUploadMedia = async (galleryId, file, mediaType) => {
    const res = await uploadWithProgress(file, mediaType !== "image", "gallery.jpg", (formData, opts) => {
      formData.append("media_type", mediaType);
      return uploadMedia(galleryId, formData, opts);
    });
    if (!res) return;
    setGalleries((prev) =>
      prev.map((g) => {
        if (g.id !== galleryId) return g;
        const items = g.items || [];
        const newItem = res.data.media_item;
        return { ...g, items: [...items, newItem] };
      })
    );
  };

  const handleUploadBlockMedia = async (blockId, file) => {
    const res = await uploadWithProgress(file, file.type?.startsWith("video/"), "media.jpg", (formData, opts) =>
      uploadBlockMedia(blockId, formData, opts)
    );
    if (!res) return;
    const updated = res.data.block;
    setBlocks((prev) => prev.map((b) => b.id === blockId ? { ...b, config: updated.config } : b));
  };

  const handleDeleteBlockMedia = (blockId, mediaType) => {
    showConfirm(t(mediaType === "video" ? "editor.deleteVideoConfirm" : "editor.deleteMediaConfirm"), async () => {
      try {
        const res = await deleteBlockMedia(blockId);
        const updated = res.data.block;
        setBlocks((prev) => prev.map((b) => b.id === blockId ? { ...b, config: updated.config } : b));
        showToast(t("editor.toast.mediaDeleted"));
        fetchStorage();
      } catch {
        showToast(t("editor.toast.mediaDeleteFailed"), "error");
      }
    });
  };

  const handleDeleteMedia = (galleryId, itemId, mediaType) => {
    showConfirm(t(mediaType === "video" ? "editor.deleteVideoConfirm" : "editor.deleteMediaConfirm"), async () => {
    try {
      await deleteMediaItem(itemId);
      setGalleries((prev) =>
        prev.map((g) => {
          if (g.id !== galleryId) return g;
          const filterItems = (arr) => (arr || []).filter((m) => m.id !== itemId);
          return {
            ...g,
            media_items: filterItems(g.media_items),
            items: filterItems(g.items),
          };
        })
      );
      showToast(t("editor.toast.mediaDeleted"));
      fetchStorage();
    } catch (err) {
      showToast(t("editor.toast.mediaDeleteFailed"), "error");
    }
    });
  };

  const handleUpdateMediaItem = async (galleryId, itemId, updates) => {
    try {
      await updateMediaItem(itemId, updates);
      setGalleries((prev) =>
        prev.map((g) => {
          if (g.id !== galleryId) return g;
          const updateItems = (arr) =>
            (arr || []).map((m) => (m.id === itemId ? { ...m, ...updates } : m));
          return {
            ...g,
            media_items: updateItems(g.media_items),
            items: updateItems(g.items),
          };
        })
      );
    } catch (err) {
      showToast(t("editor.toast.profileSaveFailed"), "error");
    }
  };

  const handleMediaDragEnd = async (galleryId, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const gallery = galleries.find((g) => g.id === galleryId);
    const items = [...(gallery?.media_items || gallery?.items || [])];
    const oldIndex = items.findIndex((m) => m.id === active.id);
    const newIndex = items.findIndex((m) => m.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(items, oldIndex, newIndex).map((m, i) => ({
      ...m,
      position: i,
    }));
    setGalleries((prev) =>
      prev.map((g) =>
        g.id === galleryId ? { ...g, media_items: reordered, items: reordered } : g
      )
    );
    try {
      await reorderMediaItems(galleryId, reordered.map((m) => m.id));
    } catch (err) {
      showToast(t("editor.toast.reorderFailed"), "error");
    }
  };

  const toggleGalleryExpanded = (galleryId) => {
    setExpandedGalleries((prev) => ({
      ...prev,
      [galleryId]: !prev[galleryId],
    }));
  };

  /* ----------------------------------------------------------
     BLOCK CONFIG HELPERS
     ---------------------------------------------------------- */

  const updateBlockConfig = (blockId, configUpdates) => {
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        const newConfig = { ...(b.config || {}), ...configUpdates };
        return { ...b, config: newConfig };
      })
    );
  };

  const saveBlockConfig = async (blockId, immediateConfig) => {
    const block = blocks.find((b) => b.id === blockId);
    if (!block) return;
    const configToSave = immediateConfig
      ? { ...(block.config || {}), ...immediateConfig }
      : block.config;
    await handleUpdateBlock(blockId, { config: configToSave });
  };

  /* ============================================================
     RENDER HELPERS
     ============================================================ */

  /* ----------------------------------------------------------
     BLOCK EDITOR: renders the edit UI for each block type
     ---------------------------------------------------------- */

  const renderBlockEditor = (block) => {
    const contacts = block.contact_items || block.contacts || [];
    const socialLinks = block.social_links || [];
    const config = block.config || {};

    switch (block.block_type) {
      /* ---- CONTACT INFO ---- */
      case "contact_info":
        return (
          <div className="space-y-3">
            {contacts.map((item) => (
              <div
                key={item.id}
                data-contact-id={item.id}
                className="flex flex-wrap items-start gap-1.5 sm:gap-2 p-2 sm:p-3 bg-gray-50 rounded-lg"
              >
                <ContactTypeSelect
                  value={item.contact_type || "email"}
                  onChange={(val) => {
                    setBlocks((prev) => prev.map((b) => {
                      const cs = b.contact_items || [];
                      if (!cs.some((c) => c.id === item.id)) return b;
                      return { ...b, contact_items: cs.map((c) => c.id === item.id ? { ...c, contact_type: val } : c) };
                    }));
                    handleUpdateContact(item.id, { contact_type: val });
                  }}
                />
                <input
                  type="text"
                  placeholder={t("editor.placeholder.label")}
                  value={item.label || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBlocks((prev) => prev.map((b) => {
                      const cs = b.contact_items || [];
                      if (!cs.some((c) => c.id === item.id)) return b;
                      return { ...b, contact_items: cs.map((c) => c.id === item.id ? { ...c, label: val } : c) };
                    }));
                  }}
                  onBlur={() => handleUpdateContact(item.id, { label: item.label || "" })}
                  className="flex-1 min-w-[60px] px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                />
                <input
                  type="text"
                  placeholder={t("editor.placeholder.value")}
                  value={item.value || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBlocks((prev) => prev.map((b) => {
                      const cs = b.contact_items || [];
                      if (!cs.some((c) => c.id === item.id)) return b;
                      return { ...b, contact_items: cs.map((c) => c.id === item.id ? { ...c, value: val } : c) };
                    }));
                  }}
                  onBlur={() => handleUpdateContact(item.id, { value: item.value || "" })}
                  className="flex-1 min-w-[60px] px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                />
                <button
                  onClick={() => handleDeleteContact(item.id)}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              onClick={() => handleAddContact(block.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition"
            >
              <Plus className="w-4 h-4" />
              {t("editor.addContact")}
            </button>
          </div>
        );

      /* ---- SOCIAL LINKS ---- */
      case "social_links":
        return (
          <div className="space-y-3">
            {socialLinks.map((link) => {
              const baseUrl = socialBaseUrls[link.platform] || "";
              const isWebsite = link.platform === "website";
              return (
                <div
                  key={link.id}
                  data-social-id={link.id}
                  className="flex flex-wrap items-center gap-1.5 sm:gap-2 p-2 sm:p-3 bg-gray-50 rounded-lg"
                >
                  <PlatformSelect
                    value={link.platform || "instagram"}
                    onChange={(val) => {
                      setBlocks((prev) => prev.map((b) => {
                        const ls = b.social_links || [];
                        if (!ls.some((l) => l.id === link.id)) return b;
                        return { ...b, social_links: ls.map((l) => l.id === link.id ? { ...l, platform: val } : l) };
                      }));
                      handleUpdateSocial(link.id, { platform: val });
                    }}
                  />
                  <div className="flex-1 min-w-0 flex items-center border border-gray-300 rounded-md bg-white overflow-hidden">
                    {baseUrl && (
                      <span className="hidden sm:inline px-2 py-1.5 text-xs text-gray-400 bg-gray-50 border-r border-gray-300 whitespace-nowrap">
                        {baseUrl}
                      </span>
                    )}
                    <input
                      type="text"
                      placeholder={isWebsite ? t("editor.placeholder.websiteUrl") : t("editor.placeholder.username")}
                      value={link.url || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setBlocks((prev) => prev.map((b) => {
                          const ls = b.social_links || [];
                          if (!ls.some((l) => l.id === link.id)) return b;
                          return { ...b, social_links: ls.map((l) => l.id === link.id ? { ...l, url: val } : l) };
                        }));
                      }}
                      onBlur={() => handleUpdateSocial(link.id, { url: link.url || "" })}
                      className="flex-1 min-w-0 px-2 py-1.5 text-sm border-none outline-none"
                    />
                  </div>
                  <button
                    onClick={() => handleDeleteSocial(link.id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
            <button
              onClick={() => handleAddSocial(block.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition"
            >
              <Plus className="w-4 h-4" />
              {t("editor.addSocialLink")}
            </button>
          </div>
        );

      /* ---- ABOUT ---- */
      case "about":
        return (
          <div className="space-y-3">
            <label className="block text-xs font-medium text-gray-500">Español</label>
            <textarea
              rows={4}
              value={config.text_i18n?.es || ""}
              onChange={(e) =>
                updateBlockConfig(block.id, { text_i18n: { ...(config.text_i18n || {}), es: e.target.value } })
              }
              onBlur={() => saveBlockConfig(block.id)}
              placeholder={t("editor.placeholder.aboutText")}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y"
            />
            <label className="block text-xs font-medium text-gray-500">English</label>
            <textarea
              rows={4}
              value={config.text_i18n?.en || ""}
              onChange={(e) =>
                updateBlockConfig(block.id, { text_i18n: { ...(config.text_i18n || {}), en: e.target.value } })
              }
              onBlur={() => saveBlockConfig(block.id)}
              placeholder="Write about yourself or your business..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y"
            />
          </div>
        );

      /* ---- CUSTOM TEXT ---- */
      case "custom_text":
      case "header":
        return <CustomTextEditor block={block} config={config} updateBlockConfig={updateBlockConfig} handleUpdateBlock={handleUpdateBlock} t={t} />;


      /* ---- CTA BUTTON ---- */
      case "cta_button":
        return (
          <div className="space-y-3">
            <input
              type="text"
              value={config.button_text || ""}
              onChange={(e) =>
                updateBlockConfig(block.id, { button_text: e.target.value })
              }
              onBlur={() => saveBlockConfig(block.id)}
              placeholder={t("editor.placeholder.buttonText")}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
            <input
              type="url"
              value={config.url || ""}
              onChange={(e) =>
                updateBlockConfig(block.id, { url: e.target.value })
              }
              onBlur={() => saveBlockConfig(block.id)}
              placeholder={t("editor.placeholder.url")}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
          </div>
        );

      /* ---- WEBSITE ---- */
      case "website":
        return (
          <div className="space-y-3">
            <input
              type="url"
              value={config.url || ""}
              onChange={(e) =>
                updateBlockConfig(block.id, { url: e.target.value })
              }
              onBlur={() => saveBlockConfig(block.id)}
              placeholder={t("editor.placeholder.websiteUrl")}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
          </div>
        );

      /* header is now handled by custom_text case above */

      /* ---- LOCATION ---- */
      case "location":
        return (
          <div className="space-y-3">
            <input
              type="text"
              value={config.address || ""}
              onChange={(e) =>
                updateBlockConfig(block.id, { address: e.target.value })
              }
              onBlur={() => saveBlockConfig(block.id)}
              placeholder={t("editor.placeholder.address")}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
          </div>
        );

      /* ---- GALLERY ---- */
      case "gallery": {
        const selectedGallery = galleries.find((g) => g.id === config.gallery_id);
        return (
          <div className="space-y-3">
            {galleries.length === 0 ? (
              <div className="text-center py-6">
                <Image className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500 mb-3">{t("editor.noGalleriesAvailable")}</p>
                <button
                  onClick={() => setActiveTab("gallery")}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {t("editor.createGallery")}
                </button>
              </div>
            ) : (
              <>
                <div className="grid gap-2">
                  {galleries.map((g) => {
                    const items = g.media_items || g.items || [];
                    const isSelected = config.gallery_id === g.id;
                    const previews = items.slice(0, 4);
                    return (
                      <button
                        key={g.id}
                        onClick={() => {
                          const val = isSelected ? "" : g.id;
                          updateBlockConfig(block.id, { gallery_id: val });
                          saveBlockConfig(block.id, { gallery_id: val });
                        }}
                        className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                          isSelected
                            ? "border-blue-500 bg-blue-50/50 ring-1 ring-blue-200"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {previews.length > 0 ? (
                            <div className="grid grid-cols-2 gap-0.5 w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                              {previews.map((item, i) => (
                                <div key={i} className="bg-gray-200 overflow-hidden">
                                  {item.media_type === "video" && !item.thumbnail_url ? (
                                    <video src={item.file_url || item.url} muted preload="metadata" className="w-full h-full object-cover" />
                                  ) : (
                                    <img src={item.thumbnail_url || item.file_url || item.url} alt="" className="w-full h-full object-cover" />
                                  )}
                                </div>
                              ))}
                              {[...Array(Math.max(0, 4 - previews.length))].map((_, i) => (
                                <div key={`empty-${i}`} className="bg-gray-100" />
                              ))}
                            </div>
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <Image className="w-5 h-5 text-gray-300" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isSelected ? "text-blue-700" : "text-gray-800"}`}>
                              {g.title}
                            </p>
                            <p className="text-xs text-gray-400">
                              {items.length} {items.length === 1 ? "item" : "items"}
                            </p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected ? "border-blue-500 bg-blue-500" : "border-gray-300"
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {selectedGallery && (
                  <button
                    onClick={() => setActiveTab("gallery")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    {t("editor.editGallery")}
                  </button>
                )}
              </>
            )}
          </div>
        );
      }

      /* ---- MEDIA (single image/video) ---- */
      case "media":
        return (
          <div className="space-y-3">
            {config.media_url ? (
              <div className="space-y-2">
                {config.media_type === "video" ? (
                  <video src={config.media_url} controls className="w-full rounded-lg" />
                ) : (
                  <img src={config.media_url} alt="" className="w-full rounded-lg" />
                )}
                <button
                  onClick={() => handleDeleteBlockMedia(block.id, config.media_type)}
                  className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> {t("editor.media.remove")}
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-400 transition">
                <Upload className="w-6 h-6 text-gray-400" />
                <span className="text-sm text-gray-500">{t("editor.media.upload")}</span>
                <span className="text-xs text-gray-400">{t("editor.media.formats")}</span>
                <span className="text-xs text-gray-400 text-center">
                  {storage
                    ? t("editor.uploadLimits", { max: maxUploadMb, free: Math.max(0, Math.floor(storage.max_mb - storage.used_mb)) })
                    : t("editor.uploadLimitsNoStorage", { max: maxUploadMb })}
                </span>
                <input
                  type="file"
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (file) handleUploadBlockMedia(block.id, file);
                  }}
                />
              </label>
            )}
          </div>
        );

      /* ---- BANK INFO ---- */
      case "bank_info":
        return (
          <div className="space-y-3">
            {[
              { key: "bank_name", tKey: "editor.bank.bankName", placeholder: "editor.bank.bankNamePlaceholder" },
              { key: "clabe", tKey: "editor.bank.clabe", placeholder: "editor.bank.clabePlaceholder" },
              { key: "account_number", tKey: "editor.bank.accountNumber", placeholder: "editor.bank.accountNumberPlaceholder" },
              { key: "card_number", tKey: "editor.bank.cardNumber", placeholder: "editor.bank.cardNumberPlaceholder" },
              { key: "beneficiary", tKey: "editor.bank.beneficiary", placeholder: "editor.bank.beneficiaryPlaceholder" },
              { key: "phone", tKey: "editor.bank.phone", placeholder: "editor.bank.phonePlaceholder" },
            ].map(({ key, tKey, placeholder }) => (
              <div key={key}>
                <label className="block text-xs text-gray-500 mb-1">{t(tKey)}</label>
                <input
                  type="text"
                  value={config[key] || ""}
                  onChange={(e) => updateBlockConfig(block.id, { [key]: e.target.value })}
                  onBlur={() => saveBlockConfig(block.id)}
                  placeholder={t(placeholder)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                />
              </div>
            ))}
          </div>
        );

      /* ---- BILLING INFO ---- */
      case "billing_info":
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("editor.billing.rfc")}</label>
              <input
                type="text"
                value={config.rfc || ""}
                onChange={(e) => updateBlockConfig(block.id, { rfc: e.target.value.toUpperCase() })}
                onBlur={() => saveBlockConfig(block.id)}
                placeholder={t("editor.billing.rfcPlaceholder")}
                maxLength={13}
                className={`w-full px-3 py-2 text-sm border rounded-lg uppercase ${
                  config.rfc && !/^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/.test(config.rfc)
                    ? "border-red-300 bg-red-50" : "border-gray-300"
                }`}
              />
              {config.rfc && !/^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/.test(config.rfc) && (
                <p className="text-xs text-red-500 mt-1">{t("editor.billing.rfcInvalid")}</p>
              )}
            </div>
            {[
              { key: "business_name", tKey: "editor.billing.businessName", placeholder: "editor.billing.businessNamePlaceholder" },
              { key: "zip_code", tKey: "editor.billing.zipCode", placeholder: "editor.billing.zipCodePlaceholder" },
            ].map(({ key, tKey, placeholder }) => (
              <div key={key}>
                <label className="block text-xs text-gray-500 mb-1">{t(tKey)}</label>
                <input
                  type="text"
                  value={config[key] || ""}
                  onChange={(e) => updateBlockConfig(block.id, { [key]: e.target.value })}
                  onBlur={() => saveBlockConfig(block.id)}
                  placeholder={t(placeholder)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                />
              </div>
            ))}
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("editor.billing.taxRegime")}</label>
              <select
                value={config.tax_regime || ""}
                onChange={(e) => {
                  updateBlockConfig(block.id, { tax_regime: e.target.value });
                  saveBlockConfig(block.id, { tax_regime: e.target.value });
                }}
                className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white ${!config.tax_regime ? "text-gray-400" : ""}`}
              >
                <option value="" disabled>{t("editor.billing.taxRegimePlaceholder")}</option>
                {[
                  "601 - General de Ley Personas Morales",
                  "603 - Personas Morales con Fines no Lucrativos",
                  "605 - Sueldos y Salarios",
                  "606 - Arrendamiento",
                  "608 - Demás ingresos",
                  "610 - Residentes en el Extranjero",
                  "612 - Personas Físicas con Actividades Empresariales y Profesionales",
                  "616 - Sin obligaciones fiscales",
                  "620 - Sociedades Cooperativas de Producción",
                  "621 - Incorporación Fiscal",
                  "622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras",
                  "623 - Opcional para Grupos de Sociedades",
                  "624 - Coordinados",
                  "625 - Régimen de Actividades Empresariales con Plataformas Tecnológicas",
                  "626 - Régimen Simplificado de Confianza",
                ].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("editor.billing.cfdiUse")}</label>
              <select
                value={config.cfdi_use || ""}
                onChange={(e) => {
                  updateBlockConfig(block.id, { cfdi_use: e.target.value });
                  saveBlockConfig(block.id, { cfdi_use: e.target.value });
                }}
                className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white ${!config.cfdi_use ? "text-gray-400" : ""}`}
              >
                <option value="" disabled>{t("editor.billing.cfdiUsePlaceholder")}</option>
                {[
                  "G01 - Adquisición de mercancías",
                  "G02 - Devoluciones, descuentos o bonificaciones",
                  "G03 - Gastos en general",
                  "D01 - Honorarios médicos",
                  "D02 - Gastos médicos por incapacidad",
                  "D03 - Gastos funerales",
                  "D04 - Donativos",
                  "D05 - Intereses por créditos hipotecarios",
                  "D06 - Aportaciones voluntarias al SAR",
                  "D07 - Primas por seguros de gastos médicos",
                  "D08 - Gastos de transportación escolar",
                  "D10 - Pagos por servicios educativos",
                  "I01 - Construcciones",
                  "I02 - Mobiliario y equipo de oficina",
                  "I03 - Equipo de transporte",
                  "I04 - Equipo de cómputo",
                  "I08 - Otra maquinaria y equipo",
                  "P01 - Por definir",
                  "S01 - Sin efectos fiscales",
                  "CP01 - Pagos",
                ].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("editor.billing.billingEmail")}</label>
              <input
                type="email"
                value={config.billing_email || ""}
                onChange={(e) => updateBlockConfig(block.id, { billing_email: e.target.value })}
                onBlur={() => saveBlockConfig(block.id)}
                placeholder={t("editor.billing.billingEmailPlaceholder")}
                className={`w-full px-3 py-2 text-sm border rounded-lg ${
                  config.billing_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(config.billing_email)
                    ? "border-red-300 bg-red-50" : "border-gray-300"
                }`}
              />
              {config.billing_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(config.billing_email) && (
                <p className="text-xs text-red-500 mt-1">{t("editor.billing.emailInvalid")}</p>
              )}
            </div>
          </div>
        );

      /* ---- CONTACT FORM ---- */
      case "contact_form":
        return (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={config.show_email !== false}
                onChange={(e) => {
                  const val = e.target.checked;
                  updateBlockConfig(block.id, { show_email: val });
                  saveBlockConfig(block.id, { show_email: val });
                }}
                className="rounded border-gray-300 text-indigo-600"
              />
              {t("contactForm.showEmail")}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={config.show_whatsapp !== false}
                onChange={(e) => {
                  const val = e.target.checked;
                  updateBlockConfig(block.id, { show_whatsapp: val });
                  saveBlockConfig(block.id, { show_whatsapp: val });
                }}
                className="rounded border-gray-300 text-indigo-600"
              />
              {t("contactForm.showWhatsapp")}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={config.show_message !== false}
                onChange={(e) => {
                  const val = e.target.checked;
                  updateBlockConfig(block.id, { show_message: val });
                  saveBlockConfig(block.id, { show_message: val });
                }}
                className="rounded border-gray-300 text-indigo-600"
              />
              {t("contactForm.showMessage")}
            </label>
            <input
              type="text"
              value={config.button_text || ""}
              onChange={(e) =>
                updateBlockConfig(block.id, { button_text: e.target.value })
              }
              onBlur={() => saveBlockConfig(block.id)}
              placeholder={t("contactForm.buttonText")}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
            <input
              type="text"
              value={config.success_message || ""}
              onChange={(e) =>
                updateBlockConfig(block.id, { success_message: e.target.value })
              }
              onBlur={() => saveBlockConfig(block.id)}
              placeholder={t("contactForm.successMessage")}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
          </div>
        );

      /* ---- GOOGLE REVIEW ---- */
      case "google_review":
        return (
          <div className="space-y-3">
            <input
              type="text"
              value={config.place_id || ""}
              onChange={(e) =>
                updateBlockConfig(block.id, { place_id: e.target.value })
              }
              onBlur={() => saveBlockConfig(block.id)}
              placeholder={t("editor.placeholder.placeId")}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
            <p className="text-xs text-gray-400">
              {t("editor.placeholder.placeIdHint")}
            </p>
          </div>
        );

      /* ---- MAP ---- */
      case "map":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("editor.map.address")}</label>
              <input
                type="text"
                value={config.address || ""}
                onChange={(e) => updateBlockConfig(block.id, { address: e.target.value })}
                onBlur={() => saveBlockConfig(block.id)}
                placeholder={t("editor.map.addressPlaceholder")}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("editor.map.lat")}</label>
                <input
                  type="text"
                  value={config.lat || ""}
                  onChange={(e) => updateBlockConfig(block.id, { lat: e.target.value })}
                  onBlur={() => saveBlockConfig(block.id)}
                  placeholder="19.4326"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("editor.map.lng")}</label>
                <input
                  type="text"
                  value={config.lng || ""}
                  onChange={(e) => updateBlockConfig(block.id, { lng: e.target.value })}
                  onBlur={() => saveBlockConfig(block.id)}
                  placeholder="-99.1332"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("editor.map.zoom")} ({config.zoom || 15})</label>
              <input
                type="range"
                min="1"
                max="19"
                value={config.zoom || 15}
                onChange={(e) => {
                  updateBlockConfig(block.id, { zoom: parseInt(e.target.value) });
                  saveBlockConfig(block.id, { zoom: parseInt(e.target.value) });
                }}
                className="w-full accent-blue-500"
              />
            </div>
            <p className="text-xs text-gray-400">{t("editor.map.hint")}</p>
            {(config.lat && config.lng) && (
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <iframe
                  title="map-preview"
                  width="100%"
                  height="200"
                  frameBorder="0"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(config.lng) - 0.01},${parseFloat(config.lat) - 0.01},${parseFloat(config.lng) + 0.01},${parseFloat(config.lat) + 0.01}&layer=mapnik&marker=${config.lat},${config.lng}`}
                  className="pointer-events-none"
                />
              </div>
            )}
          </div>
        );

      /* ---- PAYMENT ---- */
      case "payment":
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("editor.payment.accessToken")}</label>
              <input
                type="password"
                value={config.mp_access_token || ""}
                onChange={(e) => updateBlockConfig(block.id, { mp_access_token: e.target.value })}
                onBlur={() => saveBlockConfig(block.id)}
                placeholder="APP_USR-..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono"
              />
              <p className="text-xs text-gray-400 mt-1">{t("editor.payment.accessTokenHelp")}</p>
              <details className="mt-2 text-xs border border-blue-100 rounded-lg bg-blue-50/50">
                <summary className="px-3 py-2 cursor-pointer text-blue-600 font-medium hover:text-blue-700">{t("editor.payment.howToGet")}</summary>
                <div className="px-3 pb-3 space-y-1 text-gray-600">
                  {[
                    ["step1", null],
                    ["step2", "https://www.mercadopago.com.mx/accounts/profile?flow=seller"],
                    ["step3", "https://www.mercadopago.com.mx/business/integrations"],
                    ["step4", null],
                    ["step5", "https://www.mercadopago.com.mx/settings/account/credentials"],
                    ["step6", null],
                  ].map(([key, url]) => (
                    <p key={key}>
                      {t(`editor.payment.${key}`)}
                      {url && (
                        <>
                          {" "}
                          <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline break-all">{url.replace("https://www.", "")}</a>
                        </>
                      )}
                    </p>
                  ))}
                  <p className="mt-2 text-amber-600 flex items-center gap-1"><Lock className="w-3 h-3" />{t("editor.payment.warning")}</p>
                </div>
              </details>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("editor.payment.description")}</label>
              {["en", "es"].map((lang) => (
                <div key={lang} className="flex items-center gap-1.5 mb-1.5">
                  <span className="text-[10px] font-bold text-gray-300 uppercase w-5 flex-shrink-0">{lang}</span>
                  <input
                    type="text"
                    value={(config.description_i18n || {})[lang] || ""}
                    onChange={(e) => updateBlockConfig(block.id, { description_i18n: { ...(config.description_i18n || {}), [lang]: e.target.value } })}
                    onBlur={() => saveBlockConfig(block.id)}
                    placeholder={t(`editor.payment.descriptionDefault.${lang}`)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                  />
                </div>
              ))}
            </div>
            {/* Payment Items */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("editor.payment.items")}</label>
              <div className="space-y-1.5 mb-2">
                {(config.payment_items || []).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <input
                      type="text"
                      value={item.name || ""}
                      onChange={(e) => {
                        const items = [...(config.payment_items || [])];
                        items[idx] = { ...items[idx], name: e.target.value };
                        updateBlockConfig(block.id, { payment_items: items });
                      }}
                      onBlur={() => saveBlockConfig(block.id)}
                      placeholder={t("editor.payment.itemName")}
                      className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                    />
                    <div className="relative w-24 flex-shrink-0">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                      <input
                        type="number"
                        value={item.price || ""}
                        onChange={(e) => {
                          const items = [...(config.payment_items || [])];
                          items[idx] = { ...items[idx], price: e.target.value ? parseFloat(e.target.value) : "" };
                          updateBlockConfig(block.id, { payment_items: items });
                        }}
                        onBlur={() => saveBlockConfig(block.id)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className="w-full pl-5 pr-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                      />
                    </div>
                    <button
                      onClick={() => {
                        const items = (config.payment_items || []).filter((_, i) => i !== idx);
                        updateBlockConfig(block.id, { payment_items: items });
                        saveBlockConfig(block.id, { payment_items: items });
                      }}
                      className="p-1 text-gray-400 hover:text-red-500 flex-shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={() => {
                  const items = [...(config.payment_items || []), { name: "", price: "" }];
                  updateBlockConfig(block.id, { payment_items: items });
                }}
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> {t("editor.payment.addItem")}
              </button>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={config.allow_custom_amount !== false}
                onChange={(e) => {
                  updateBlockConfig(block.id, { allow_custom_amount: e.target.checked });
                  saveBlockConfig(block.id, { allow_custom_amount: e.target.checked });
                }}
                className="rounded"
              />
              {t("editor.payment.allowCustomAmount")}
            </label>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("editor.payment.installments")}</label>
              <select
                value={config.max_installments || 1}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  updateBlockConfig(block.id, { max_installments: val });
                  saveBlockConfig(block.id, { max_installments: val });
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
              >
                <option value={1}>{t("editor.payment.installmentsNone")}</option>
                <option value={3}>3 {t("editor.payment.installmentsMonths")}</option>
                <option value={6}>6 {t("editor.payment.installmentsMonths")}</option>
                <option value={9}>9 {t("editor.payment.installmentsMonths")}</option>
                <option value={12}>12 {t("editor.payment.installmentsMonths")}</option>
                <option value={18}>18 {t("editor.payment.installmentsMonths")}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("editor.payment.buttonText")}</label>
              <input
                type="text"
                value={config.button_text || ""}
                onChange={(e) => updateBlockConfig(block.id, { button_text: e.target.value })}
                onBlur={() => saveBlockConfig(block.id)}
                placeholder={t("editor.payment.buttonTextPlaceholder")}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
              />
            </div>
            {config.mp_access_token && (
              <div className="flex items-center gap-2 text-xs text-green-600">
                <Check className="w-3 h-3" />
                {t("editor.payment.configured")}
              </div>
            )}
          </div>
        );

      case "appointment": {
        const DAYS = [
          { key: "mon", label: t("editor.appointment.mon") },
          { key: "tue", label: t("editor.appointment.tue") },
          { key: "wed", label: t("editor.appointment.wed") },
          { key: "thu", label: t("editor.appointment.thu") },
          { key: "fri", label: t("editor.appointment.fri") },
          { key: "sat", label: t("editor.appointment.sat") },
          { key: "sun", label: t("editor.appointment.sun") },
        ];
        const schedule = config.schedule || {};
        const services = config.service_types || [];
        return (
          <div className="space-y-4">
            {/* Schedule */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> {t("editor.appointment.schedule")}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {DAYS.map(({ key, label }) => {
                  const daySlots = schedule[key] || [];
                  const enabled = daySlots.length > 0;
                  return (
                    <div key={key} className="border border-gray-200 rounded-lg p-1.5">
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => {
                            const newSchedule = { ...schedule };
                            if (e.target.checked) {
                              newSchedule[key] = [{ start: "09:00", end: "17:00" }];
                            } else {
                              delete newSchedule[key];
                            }
                            updateBlockConfig(block.id, { schedule: newSchedule });
                            saveBlockConfig(block.id, { schedule: newSchedule });
                          }}
                          className="rounded w-3.5 h-3.5"
                        />
                        <span className="font-medium">{label}</span>
                      </label>
                      {enabled && (
                        <div className="mt-1 space-y-1">
                          {daySlots.map((slot, si) => (
                            <div key={si} className="flex items-center gap-1">
                              <input
                                type="time"
                                value={slot.start}
                                onChange={(e) => {
                                  const newSchedule = { ...schedule };
                                  newSchedule[key] = [...daySlots];
                                  newSchedule[key][si] = { ...slot, start: e.target.value };
                                  updateBlockConfig(block.id, { schedule: newSchedule });
                                }}
                                onBlur={() => saveBlockConfig(block.id)}
                                className="px-1 py-0.5 text-[11px] border border-gray-300 rounded w-[4.5rem] min-w-0"
                              />
                              <span className="text-gray-400 text-[10px]">-</span>
                              <input
                                type="time"
                                value={slot.end}
                                onChange={(e) => {
                                  const newSchedule = { ...schedule };
                                  newSchedule[key] = [...daySlots];
                                  newSchedule[key][si] = { ...slot, end: e.target.value };
                                  updateBlockConfig(block.id, { schedule: newSchedule });
                                }}
                                onBlur={() => saveBlockConfig(block.id)}
                                className="px-1 py-0.5 text-[11px] border border-gray-300 rounded w-[4.5rem] min-w-0"
                              />
                              <button
                                onClick={() => {
                                  const newSchedule = { ...schedule };
                                  newSchedule[key] = daySlots.filter((_, i) => i !== si);
                                  if (newSchedule[key].length === 0) delete newSchedule[key];
                                  updateBlockConfig(block.id, { schedule: newSchedule });
                                  saveBlockConfig(block.id, { schedule: newSchedule });
                                }}
                                className="p-0.5 text-gray-400 hover:text-red-500 flex-shrink-0"
                              >
                                <Trash2 className="w-2.5 h-2.5" />
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={() => {
                              const newSchedule = { ...schedule };
                              newSchedule[key] = [...daySlots, { start: "14:00", end: "18:00" }];
                              updateBlockConfig(block.id, { schedule: newSchedule });
                              saveBlockConfig(block.id, { schedule: newSchedule });
                            }}
                            className="text-[10px] text-blue-600 hover:text-blue-700 flex items-center gap-0.5"
                          >
                            <Plus className="w-2.5 h-2.5" /> {t("editor.appointment.addRange")}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Service Types */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-2">
                {t("editor.appointment.services")}
              </label>
              <div className="space-y-2 mb-2">
                {services.map((svc, idx) => (
                  <div key={svc.id || idx} className="border border-gray-200 rounded-lg p-2.5 space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        value={svc.name || ""}
                        onChange={(e) => {
                          const updated = [...services];
                          updated[idx] = { ...svc, name: e.target.value };
                          updateBlockConfig(block.id, { service_types: updated });
                        }}
                        onBlur={() => saveBlockConfig(block.id)}
                        placeholder={t("editor.appointment.serviceName")}
                        className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                      />
                      <button
                        onClick={() => {
                          const updated = services.filter((_, i) => i !== idx);
                          updateBlockConfig(block.id, { service_types: updated });
                          saveBlockConfig(block.id, { service_types: updated });
                        }}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex gap-1.5">
                      <div className="flex-1">
                        <label className="block text-[10px] text-gray-400 mb-0.5">{t("editor.appointment.duration")}</label>
                        <div className="relative">
                          <input
                            type="number"
                            value={svc.duration ?? ""}
                            onChange={(e) => {
                              const updated = [...services];
                              updated[idx] = { ...svc, duration: e.target.value === "" ? "" : parseInt(e.target.value) };
                              updateBlockConfig(block.id, { service_types: updated });
                            }}
                            onBlur={(e) => {
                              const v = e.target.value === "" ? 30 : parseInt(e.target.value);
                              const updated = [...services];
                              updated[idx] = { ...svc, duration: v };
                              updateBlockConfig(block.id, { service_types: updated });
                              saveBlockConfig(block.id, { service_types: updated });
                            }}
                            placeholder="30"
                            min="5"
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded pr-8"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">min</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <label className="block text-[10px] text-gray-400 mb-0.5">{t("editor.appointment.price")}</label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                          <input
                            type="number"
                            value={svc.price ?? ""}
                            onChange={(e) => {
                              const updated = [...services];
                              updated[idx] = { ...svc, price: e.target.value === "" ? "" : parseFloat(e.target.value) };
                              updateBlockConfig(block.id, { service_types: updated });
                            }}
                            onBlur={(e) => {
                              const v = e.target.value === "" ? 0 : parseFloat(e.target.value);
                              const updated = [...services];
                              updated[idx] = { ...svc, price: v };
                              updateBlockConfig(block.id, { service_types: updated });
                              saveBlockConfig(block.id, { service_types: updated });
                            }}
                            placeholder="0"
                            min="0"
                            step="0.01"
                            className="w-full pl-5 pr-2 py-1 text-xs border border-gray-300 rounded"
                          />
                        </div>
                      </div>
                    </div>
                    <input
                      type="text"
                      value={svc.description || ""}
                      onChange={(e) => {
                        const updated = [...services];
                        updated[idx] = { ...svc, description: e.target.value };
                        updateBlockConfig(block.id, { service_types: updated });
                      }}
                      onBlur={() => saveBlockConfig(block.id)}
                      placeholder={t("editor.appointment.serviceDescription")}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded-lg"
                    />
                  </div>
                ))}
              </div>
              <button
                onClick={() => {
                  const newSvc = { id: `svc_${Date.now()}`, name: "", duration: 30, price: 0, description: "" };
                  const updated = [...services, newSvc];
                  updateBlockConfig(block.id, { service_types: updated });
                  saveBlockConfig(block.id, { service_types: updated });
                }}
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> {t("editor.appointment.addService")}
              </button>
            </div>

            {/* Settings */}
            <div className="space-y-3 border-t border-gray-100 pt-3">
              <label className="block text-xs font-semibold text-gray-500 mb-1">{t("editor.appointment.settings")}</label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] text-gray-400 mb-0.5">{t("editor.appointment.slotDuration")}</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={config.slot_duration ?? 30}
                      onChange={(e) => updateBlockConfig(block.id, { slot_duration: e.target.value === "" ? "" : parseInt(e.target.value) })}
                      onBlur={(e) => {
                        const v = e.target.value === "" ? 30 : parseInt(e.target.value);
                        updateBlockConfig(block.id, { slot_duration: v });
                        saveBlockConfig(block.id, { slot_duration: v });
                      }}
                      min="5"
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded pr-8"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">min</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-400 mb-0.5">{t("editor.appointment.bufferTime")}</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={config.buffer_time ?? 0}
                      onChange={(e) => updateBlockConfig(block.id, { buffer_time: e.target.value === "" ? "" : parseInt(e.target.value) })}
                      onBlur={(e) => {
                        const v = e.target.value === "" ? 0 : parseInt(e.target.value);
                        updateBlockConfig(block.id, { buffer_time: v });
                        saveBlockConfig(block.id, { buffer_time: v });
                      }}
                      min="0"
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded pr-8"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">min</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-400 mb-0.5">{t("editor.appointment.minNotice")}</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={config.min_notice_minutes ?? 60}
                      onChange={(e) => updateBlockConfig(block.id, { min_notice_minutes: e.target.value === "" ? "" : parseInt(e.target.value) })}
                      onBlur={(e) => {
                        const v = e.target.value === "" ? 60 : parseInt(e.target.value);
                        updateBlockConfig(block.id, { min_notice_minutes: v });
                        saveBlockConfig(block.id, { min_notice_minutes: v });
                      }}
                      min="0"
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded pr-8"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">min</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-400 mb-0.5">{t("editor.appointment.bookingWindow")}</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={config.booking_window_days ?? 30}
                      onChange={(e) => updateBlockConfig(block.id, { booking_window_days: e.target.value === "" ? "" : parseInt(e.target.value) })}
                      onBlur={(e) => {
                        const v = e.target.value === "" ? 30 : parseInt(e.target.value);
                        updateBlockConfig(block.id, { booking_window_days: v });
                        saveBlockConfig(block.id, { booking_window_days: v });
                      }}
                      min="1"
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded pr-8"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">{t("editor.appointment.days")}</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">{t("editor.appointment.confirmationMode")}</label>
                <select
                  value={config.confirmation_mode || "auto"}
                  onChange={(e) => {
                    updateBlockConfig(block.id, { confirmation_mode: e.target.value });
                    saveBlockConfig(block.id, { confirmation_mode: e.target.value });
                  }}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded"
                >
                  <option value="auto">{t("editor.appointment.confirmAuto")}</option>
                  <option value="manual">{t("editor.appointment.confirmManual")}</option>
                </select>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-2 border-t border-gray-100 pt-3">
              <label className="block text-xs font-semibold text-gray-500 mb-1">{t("editor.appointment.location")}</label>
              <select
                value={config.location_type || ""}
                onChange={(e) => {
                  updateBlockConfig(block.id, { location_type: e.target.value });
                  saveBlockConfig(block.id, { location_type: e.target.value });
                }}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded"
              >
                <option value="">{t("editor.appointment.locationNone")}</option>
                <option value="address">{t("editor.appointment.locationAddress")}</option>
                <option value="video">{t("editor.appointment.locationVideo")}</option>
                <option value="phone">{t("editor.appointment.locationPhone")}</option>
              </select>
              {config.location_type === "address" && (
                <input
                  type="text"
                  value={config.location_address || ""}
                  onChange={(e) => updateBlockConfig(block.id, { location_address: e.target.value })}
                  onBlur={() => saveBlockConfig(block.id)}
                  placeholder={t("editor.appointment.addressPlaceholder")}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded"
                />
              )}
              {config.location_type === "video" && (
                <input
                  type="url"
                  value={config.location_link || ""}
                  onChange={(e) => updateBlockConfig(block.id, { location_link: e.target.value })}
                  onBlur={() => saveBlockConfig(block.id)}
                  placeholder="https://zoom.us/j/..."
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded"
                />
              )}
            </div>
          </div>
        );
      }

      default:
        return (
          <p className="text-sm text-gray-500">
            {t("editor.noEditor")}
          </p>
        );
    }
  };

  /* ============================================================
     LOADING STATE
     ============================================================ */

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">{t("editor.loading")}</p>
        </div>
      </div>
    );
  }

  /* ============================================================
     MAIN RENDER
     ============================================================ */

  return (
    <div className="min-h-screen bg-gray-50 -m-4 sm:-m-6">
      {/* ---- TOAST ---- */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* ---- IMAGE SOURCE PICKER ---- */}
      {imageSourcePicker && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={() => setImageSourcePicker(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-sm mx-4 mb-0 sm:mb-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100 text-center">
              <h3 className="text-sm font-semibold text-gray-900">{t("editor.imageSource")}</h3>
            </div>
            <div className="p-2">
              <button
                onClick={() => {
                  setImageSourcePicker(null);
                  if (cameraInputRef.current) {
                    cameraInputRef.current.setAttribute("data-type", imageSourcePicker);
                    cameraInputRef.current.click();
                  }
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-xl transition"
              >
                <Camera className="w-5 h-5 text-gray-500" />
                {t("editor.takePhoto")}
              </button>
              <button
                onClick={() => {
                  setImageSourcePicker(null);
                  const ref = imageSourcePicker === "profile" ? profileImageRef : imageSourcePicker === "cover" ? coverImageRef : pwaIconRef;
                  ref.current?.click();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-xl transition"
              >
                <Upload className="w-5 h-5 text-gray-500" />
                {t("editor.chooseFromGallery")}
              </button>
            </div>
            <div className="p-2 border-t border-gray-100">
              <button
                onClick={() => setImageSourcePicker(null)}
                className="w-full px-4 py-3 text-sm font-medium text-gray-500 hover:bg-gray-50 rounded-xl transition"
              >
                {t("editor.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const type = cameraInputRef.current?.getAttribute("data-type") || "profile";
          handleImageSelect(e, type);
        }}
      />

      {/* ---- CROP MODAL ---- */}
      {cropModal && (
        <ImageCropModal
          imageSrc={cropModal.src}
          aspect={cropModal.type === "profile" ? 1 : 16 / 5}
          onConfirm={handleCroppedUpload}
          onCancel={() => setCropModal(null)}
          labels={{ zoom: t("editor.zoom"), cancel: t("editor.cropCancel"), cropUpload: t("editor.cropUpload") }}
        />
      )}

      {/* ---- QR MODAL ---- */}
      {qrModalOpen && (
        <QRModal
          url={`${profileUrl}?ref=qr`}
          slug={profile.slug}
          onClose={() => setQrModalOpen(false)}
          labels={{ qrCode: t("editor.qrCode"), downloadPng: t("editor.downloadPng") }}
          showLogo={profile.settings?.qr_show_logo}
          logoUrl={profile.profile_image_url}
          fgColor={profile.settings?.qr_use_color ? profile.theme_color : "#000000"}
        />
      )}

      {/* ---- SLUG CHANGE CONFIRMATION MODAL ---- */}
      {slugChangeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSlugChangeModal(false)} />
          <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 sm:p-8 shadow-xl mx-6 overflow-hidden">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <svg className="h-7 w-7 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900">{t("editor.slugChangeWarning")}</h3>
              <p className="mt-2 text-sm text-gray-500">{t("editor.slugChangeDesc")}</p>
            </div>

            <div className="mt-5">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                {t("editor.slugChangeCurrent")}
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-400 line-through break-all">
                {baseUrl}/p/{originalSlug}
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                {t("editor.slugChangeNew")}
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 break-all">
                {baseUrl}/p/
              </div>
              <input
                type="text"
                value={profile.slug}
                onChange={(e) =>
                  setProfile((prev) => ({
                    ...prev,
                    slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                  }))
                }
                autoFocus
                className="w-full mt-1.5 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setProfile((prev) => ({ ...prev, slug: originalSlug }));
                  setSlugChangeModal(false);
                }}
                className="rounded-lg px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 transition"
              >
                {t("editor.slugChangeCancel")}
              </button>
              <button
                onClick={() => {
                  if (profile.slug && profile.slug !== originalSlug) {
                    handleSave();
                  }
                }}
                disabled={!profile.slug || profile.slug === originalSlug}
                className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {t("editor.slugChangeConfirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          TOP BAR
          ============================================================ */}
      <div id="editor-toolbar" className="fixed top-14 right-0 z-40 bg-white shadow-sm left-0 md:left-56">
        <div className="border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 gap-2 sm:gap-4">
            <button
              onClick={() => navigate("/app/profiles")}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            {/* Profile name (editable inline) */}
            <input
              type="text"
              value={profile.name}
              onChange={(e) =>
                setProfile((prev) => ({ ...prev, name: e.target.value }))
              }
              className="text-base sm:text-lg font-bold text-gray-900 bg-transparent border-none outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded px-1 sm:px-2 py-1 min-w-0 flex-1"
              placeholder={t("editor.profileNamePlaceholder")}
            />

            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-2.5 sm:px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
              >
                <Save className="w-4 h-4" />
                <span className="hidden sm:inline">{saving ? t("editor.saving") : t("editor.save")}</span>
              </button>

              {/* Preview Button */}
              <button
                onClick={() => navigate(`/app/preview/${profile.slug}`)}
                className="flex items-center gap-1.5 px-2.5 sm:px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition"
              >
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">{t("editor.preview")}</span>
              </button>

              {/* Stats Button */}
              <button
                onClick={() => navigate(`/app/profile/${profile.slug}/stats`)}
                className="flex items-center gap-1.5 px-2.5 sm:px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition"
              >
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">{t("editor.tab.stats")}</span>
              </button>

              {/* Share Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShareDropdownOpen(!shareDropdownOpen)}
                  className="flex items-center gap-1.5 px-2.5 sm:px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition"
                >
                  <Share2 className="w-4 h-4" />
                  <span className="hidden sm:inline">{t("editor.share")}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>

                {shareDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShareDropdownOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                      <button
                        onClick={() => {
                          handleCopyUrl();
                          setShareDropdownOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Copy className="w-4 h-4" />
                        {t("editor.copyUrl")}
                      </button>
                      <button
                        onClick={() => {
                          setQrModalOpen(true);
                          setShareDropdownOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <QrCode className="w-4 h-4" />
                        {t("editor.qrCode")}
                      </button>
                      <button
                        onClick={() => {
                          handleDownloadVCard();
                          setShareDropdownOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Download className="w-4 h-4" />
                        {t("editor.downloadVcard")}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* TAB NAVIGATION */}
        <div className="border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative">
              {/* Left scroll arrow */}
              {tabScrollState.left && (
                <button
                  onClick={() => {
                    const nav = tabNavRef.current;
                    if (nav) nav.scrollBy({ left: -120, behavior: "smooth" });
                  }}
                  className="absolute left-0 top-0 bottom-0 z-10 flex items-center pl-0.5 pr-2 bg-gradient-to-r from-white via-white to-transparent"
                >
                  <ChevronDown className="w-4 h-4 text-gray-400 rotate-90" />
                </button>
              )}
              <nav
                ref={tabNavRef}
                onScroll={updateTabScroll}
                className="flex gap-1 overflow-x-auto scrollbar-hide"
                role="tablist"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {TABS.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition ${
                        isActive
                          ? "border-blue-600 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
              {/* Right scroll arrow */}
              {tabScrollState.right && (
                <button
                  onClick={() => {
                    const nav = tabNavRef.current;
                    if (nav) nav.scrollBy({ left: 120, behavior: "smooth" });
                  }}
                  className="absolute right-0 top-0 bottom-0 z-10 flex items-center pr-0.5 pl-2 bg-gradient-to-l from-white via-white to-transparent"
                >
                  <ChevronDown className="w-4 h-4 text-gray-400 -rotate-90" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Spacer for fixed toolbar */}
      <div className="h-[6.5rem] sm:h-28" />

      {/* ============================================================
          TAB CONTENT
          ============================================================ */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Storage Bar */}
        {storage && (
          <div className="mb-6 p-4 bg-white border border-gray-200 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">{t("editor.storage")}</span>
              <span className="text-xs text-gray-500">
                {storage.used_mb} MB / {storage.max_mb} MB
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
              <div
                className={`h-2.5 rounded-full transition-all duration-500 ${
                  storage.used_mb / storage.max_mb > 0.9
                    ? "bg-red-500"
                    : storage.used_mb / storage.max_mb > 0.7
                    ? "bg-amber-500"
                    : "bg-blue-500"
                }`}
                style={{ width: `${Math.min(100, (storage.used_mb / storage.max_mb) * 100)}%` }}
              />
            </div>
            {storage.used_mb / storage.max_mb > 0.9 && (
              <p className="text-xs text-red-500 mt-1.5">{t("editor.storageAlmostFull")}</p>
            )}
          </div>
        )}

        {/* ============================================================
            GENERAL TAB
            ============================================================ */}
        {activeTab === "general" && (
          <div className="space-y-8">
            {/* Active Toggle */}
            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl">
              <div>
                <div className="font-medium text-gray-900">{t("editor.profileStatus")}</div>
                <div className="text-sm text-gray-500">
                  {profile.is_active
                    ? t("editor.statusActive")
                    : t("editor.statusInactive")}
                </div>
              </div>
              <button
                onClick={async () => {
                  const newVal = !profile.is_active;
                  setProfile((prev) => ({ ...prev, is_active: newVal }));
                  try {
                    await updateProfile(id, { is_active: newVal });
                  } catch (err) {
                    setProfile((prev) => ({ ...prev, is_active: !newVal }));
                    showToast(t("editor.toast.profileSaveFailed"), "error");
                  }
                }}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  profile.is_active ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    profile.is_active ? "translate-x-6" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            {/* Reset from Template (org profiles only) */}
            {profile.organization_id && (
              <>
                <button
                  onClick={() => setResetModal(true)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 transition w-full"
                >
                  <RotateCcw className="w-4 h-4" />
                  {t("reset.title")}
                </button>

                {resetModal && (
                  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">{t("reset.title")}</h2>
                      <div className="space-y-4">
                        {/* Structure reset */}
                        <div className="p-4 border border-gray-200 rounded-xl">
                          <h3 className="font-medium text-gray-900 mb-1">{t("reset.structureTitle")}</h3>
                          <p className="text-sm text-gray-500 mb-3">{t("reset.structureDesc")}</p>
                          <button
                            disabled={resetting}
                            onClick={async () => {
                              setResetting(true);
                              try {
                                const res = await resetProfile(id, "structure");
                                setProfile(res.data.profile);
                                setBlocks(res.data.profile.blocks || []);
                                showToast(t("reset.success"));
                                setResetModal(false);
                              } catch {
                                showToast(t("reset.failed"), "error");
                              } finally {
                                setResetting(false);
                              }
                            }}
                            className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 transition"
                          >
                            {t("reset.structureTitle")}
                          </button>
                        </div>

                        {/* Full reset */}
                        <div className="p-4 border border-red-200 rounded-xl bg-red-50/50">
                          <h3 className="font-medium text-gray-900 mb-1">{t("reset.fullTitle")}</h3>
                          <p className="text-sm text-gray-500 mb-3">{t("reset.fullDesc")}</p>
                          <button
                            disabled={resetting}
                            onClick={async () => {
                              setResetting(true);
                              try {
                                const res = await resetProfile(id, "full");
                                setProfile(res.data.profile);
                                setBlocks(res.data.profile.blocks || []);
                                showToast(t("reset.success"));
                                setResetModal(false);
                              } catch {
                                showToast(t("reset.failed"), "error");
                              } finally {
                                setResetting(false);
                              }
                            }}
                            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition"
                          >
                            {t("reset.fullTitle")}
                          </button>
                        </div>
                      </div>

                      <div className="flex justify-end mt-4">
                        <button
                          onClick={() => setResetModal(false)}
                          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition"
                        >
                          {t("reset.cancel")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Profile Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">
                {t("editor.profileName")}
              </label>
              <input
                type="text"
                value={profile.name}
                onChange={(e) =>
                  setProfile((prev) => ({ ...prev, name: e.target.value }))
                }
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t("editor.profileNamePlaceholder")}
              />
            </div>

            {/* Job Title (org profiles) */}
            {profile.organization_id && (
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">
                  {t("editor.jobTitle")}
                </label>
                <input
                  type="text"
                  value={profile.job_title || ""}
                  onChange={(e) =>
                    setProfile((prev) => ({ ...prev, job_title: e.target.value }))
                  }
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t("editor.jobTitlePlaceholder")}
                />
              </div>
            )}

            {/* Tagline */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">
                {t("editor.tagline")}
              </label>
              {profile.organization_id ? (
                <div className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-500 bg-gray-50">
                  {profile.tagline || <span className="italic text-gray-400">{t("editor.taglineFromTemplate")}</span>}
                </div>
              ) : (
                <input
                  type="text"
                  value={profile.tagline}
                  onChange={(e) =>
                    setProfile((prev) => ({ ...prev, tagline: e.target.value }))
                  }
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t("editor.taglinePlaceholder")}
                />
              )}
            </div>

            {/* Slug */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">
                {t("editor.urlSlug")}
              </label>
              <div className="flex items-center gap-0">
                <span className="px-3 py-2.5 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-sm text-gray-500">
                  {baseUrl}/p/
                </span>
                <input
                  type="text"
                  value={profile.slug}
                  onFocus={() => {
                    if (originalSlug) setSlugChangeModal(true);
                  }}
                  readOnly={!!originalSlug}
                  className={`flex-1 px-3 py-2.5 border border-gray-300 rounded-r-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${originalSlug ? "cursor-pointer" : ""}`}
                  placeholder={t("editor.urlSlugPlaceholder")}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Live preview:{" "}
                <a
                  href={profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {profileUrl}
                </a>
              </p>
              {/* First time slug advisory */}
              {profile.slug && !originalSlug && (
                <p className="mt-1 text-xs text-gray-400">
                  {t("editor.slugAdvisory")}
                </p>
              )}
            </div>

            {/* Profile Type Selector */}
            {!profile.organization_id && (
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold text-gray-900">
                  {t("editor.profileType")}
                </span>
                <div className="flex gap-1.5">
                  {PROFILE_TYPES.map((pt) => (
                    <button
                      key={pt.value}
                      onClick={() =>
                        setProfile((prev) => ({
                          ...prev,
                          profile_type: pt.value,
                        }))
                      }
                      title={pt.desc}
                      className={`px-3 py-1.5 text-sm rounded-full border transition ${
                        profile.profile_type === pt.value
                          ? "border-blue-600 bg-blue-50 text-blue-700 font-medium"
                          : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Info */}
            {(() => {
              const contactBlocks = blocks.filter((b) => b.block_type === "contact_info");
              if (contactBlocks.length === 0) return (
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{t("editor.blockType.contact_info")}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{t("editor.noContactBlock")}</p>
                    </div>
                    <button
                      onClick={() => handleAddBlock("contact_info")}
                      className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
                    >
                      <Plus className="w-4 h-4" />
                      {t("editor.create")}
                    </button>
                  </div>
                </div>
              );
              return contactBlocks.map((contactBlock) => {
                const contacts = contactBlock.contact_items || contactBlock.contacts || [];
                return (
                  <div key={contactBlock.id} className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-gray-900">
                      <Phone className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                      {contactBlock.title || t("editor.blockType.contact_info")}
                    </h3>
                    <p className="text-xs text-gray-400 mt-1 mb-4">
                      {t("editor.hint.blockMirrorBefore")}{" "}
                      <button
                        onClick={() => {
                          setActiveTab("blocks");
                          setExpandedBlocks((prev) => ({ ...prev, [contactBlock.id]: true }));
                          setTimeout(() => {
                            const el = document.querySelector(`[data-block-id="${contactBlock.id}"]`);
                            if (el) {
                              const y = el.getBoundingClientRect().top + window.scrollY - 180;
                              window.scrollTo({ top: y, behavior: "smooth" });
                            }
                          }, 150);
                        }}
                        className="inline-flex items-center gap-0.5 text-blue-500 hover:text-blue-700 font-medium hover:underline transition"
                      >
                        {t("editor.hint.goToBlock")}
                        <ExternalLink className="w-3 h-3" />
                      </button>{" "}
                      {t("editor.hint.blockMirrorAfter")}
                    </p>
                    <div className="space-y-3">
                      {contacts.map((item) => (
                        <div key={item.id} data-contact-id={item.id} className="flex flex-wrap items-start gap-1.5 sm:gap-2 p-2 sm:p-3 bg-gray-50 rounded-lg">
                          <ContactTypeSelect
                            value={item.contact_type || "email"}
                            onChange={(val) => {
                              setBlocks((prev) => prev.map((b) => {
                                const cs = b.contact_items || [];
                                if (!cs.some((c) => c.id === item.id)) return b;
                                return { ...b, contact_items: cs.map((c) => c.id === item.id ? { ...c, contact_type: val } : c) };
                              }));
                              handleUpdateContact(item.id, { contact_type: val });
                            }}
                          />
                          <input
                            type="text"
                            placeholder={t("editor.placeholder.label")}
                            value={item.label || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setBlocks((prev) => prev.map((b) => {
                                const cs = b.contact_items || [];
                                if (!cs.some((c) => c.id === item.id)) return b;
                                return { ...b, contact_items: cs.map((c) => c.id === item.id ? { ...c, label: val } : c) };
                              }));
                            }}
                            onBlur={() => handleUpdateContact(item.id, { label: item.label || "" })}
                            className="flex-1 min-w-[60px] px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                          />
                          <input
                            type="text"
                            placeholder={t("editor.placeholder.value")}
                            value={item.value || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setBlocks((prev) => prev.map((b) => {
                                const cs = b.contact_items || [];
                                if (!cs.some((c) => c.id === item.id)) return b;
                                return { ...b, contact_items: cs.map((c) => c.id === item.id ? { ...c, value: val } : c) };
                              }));
                            }}
                            onBlur={() => handleUpdateContact(item.id, { value: item.value || "" })}
                            className="flex-1 min-w-[60px] px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                          />
                          <button
                            onClick={() => handleDeleteContact(item.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => handleAddContact(contactBlock.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition"
                      >
                        <Plus className="w-4 h-4" />
                        {t("editor.addContact")}
                      </button>
                    </div>
                  </div>
                );
              });
            })()}

            {/* Social Links */}
            {(() => {
              const socialBlocks = blocks.filter((b) => b.block_type === "social_links");
              if (socialBlocks.length === 0) return (
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{t("editor.blockType.social_links")}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{t("editor.noSocialBlock")}</p>
                    </div>
                    <button
                      onClick={() => handleAddBlock("social_links")}
                      className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
                    >
                      <Plus className="w-4 h-4" />
                      {t("editor.create")}
                    </button>
                  </div>
                </div>
              );
              return socialBlocks.map((socialBlock) => {
                const socialLinks = socialBlock.social_links || [];
                return (
                  <div key={socialBlock.id} className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-gray-900">
                      <Globe className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                      {socialBlock.title || t("editor.blockType.social_links")}
                    </h3>
                    <p className="text-xs text-gray-400 mt-1 mb-4">
                      {t("editor.hint.blockMirrorBefore")}{" "}
                      <button
                        onClick={() => {
                          setActiveTab("blocks");
                          setExpandedBlocks((prev) => ({ ...prev, [socialBlock.id]: true }));
                          setTimeout(() => {
                            const el = document.querySelector(`[data-block-id="${socialBlock.id}"]`);
                            if (el) {
                              const y = el.getBoundingClientRect().top + window.scrollY - 180;
                              window.scrollTo({ top: y, behavior: "smooth" });
                            }
                          }, 150);
                        }}
                        className="inline-flex items-center gap-0.5 text-blue-500 hover:text-blue-700 font-medium hover:underline transition"
                      >
                        {t("editor.hint.goToBlock")}
                        <ExternalLink className="w-3 h-3" />
                      </button>{" "}
                      {t("editor.hint.blockMirrorAfter")}
                    </p>
                    <div className="space-y-3">
                      {socialLinks.map((link) => {
                        const baseUrl = socialBaseUrls[link.platform] || "";
                        const isWebsite = link.platform === "website";
                        return (
                          <div key={link.id} data-social-id={link.id} className="flex flex-wrap items-center gap-1.5 sm:gap-2 p-2 sm:p-3 bg-gray-50 rounded-lg">
                            <PlatformSelect
                              value={link.platform || "instagram"}
                              onChange={(val) => {
                                setBlocks((prev) => prev.map((b) => {
                                  const ls = b.social_links || [];
                                  if (!ls.some((l) => l.id === link.id)) return b;
                                  return { ...b, social_links: ls.map((l) => l.id === link.id ? { ...l, platform: val } : l) };
                                }));
                                handleUpdateSocial(link.id, { platform: val });
                              }}
                            />
                            <div className="flex-1 min-w-0 flex items-center border border-gray-300 rounded-md bg-white overflow-hidden">
                              {baseUrl && (
                                <span className="hidden sm:inline px-2 py-1.5 text-xs text-gray-400 bg-gray-50 border-r border-gray-300 whitespace-nowrap">
                                  {baseUrl}
                                </span>
                              )}
                              <input
                                type="text"
                                placeholder={isWebsite ? t("editor.placeholder.websiteUrl") : t("editor.placeholder.username")}
                                value={link.url || ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setBlocks((prev) => prev.map((b) => {
                                    const ls = b.social_links || [];
                                    if (!ls.some((l) => l.id === link.id)) return b;
                                    return { ...b, social_links: ls.map((l) => l.id === link.id ? { ...l, url: val } : l) };
                                  }));
                                }}
                                onBlur={() => handleUpdateSocial(link.id, { url: link.url || "" })}
                                className="flex-1 min-w-0 px-2 py-1.5 text-sm border-none outline-none"
                              />
                            </div>
                            <button
                              onClick={() => handleDeleteSocial(link.id)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                      <button
                        onClick={() => handleAddSocial(socialBlock.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition"
                      >
                        <Plus className="w-4 h-4" />
                        {t("editor.addSocialLink")}
                      </button>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* ============================================================
            APPEARANCE TAB
            ============================================================ */}
        {activeTab === "appearance" && (
          <div className="space-y-8">
            {/* Profile Image Upload */}
            <div id="upload-profile">
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                {t("editor.profileImage")}
              </label>
              <div className="flex items-center gap-6">
                <div
                  onClick={() => !uploadProgress && setImageSourcePicker("profile")}
                  className={`relative w-28 h-28 rounded-full border-2 border-dashed flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition overflow-hidden bg-gray-50 ${highlightUpload === "profile" ? "border-blue-500 ring-4 ring-blue-200 animate-pulse" : "border-gray-300"}`}
                >
                  {profile.profile_image_url ? (
                    <img
                      src={profile.profile_image_url}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center text-gray-400">
                      <Upload className="w-6 h-6 mb-1" />
                      <span className="text-xs">{t("editor.upload")}</span>
                    </div>
                  )}
                  {uploadProgress?.type === "profile" && (
                    <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center rounded-full">
                      <span className="text-white text-sm font-bold">{uploadProgress.progress}%</span>
                      {uploadProgress.fileSize > 0 && <span className="text-white/70 text-[10px]">{formatBytes(uploadProgress.loaded || 0)} / {formatBytes(uploadProgress.fileSize)}</span>}
                      <button onClick={(e) => { e.stopPropagation(); cancelUpload(); }} className="mt-1 text-white/80 hover:text-white text-xs underline">{t("editor.cancel")}</button>
                    </div>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  <p>{t("editor.uploadProfilePhoto")}</p>
                  <p className="text-xs mt-1">
                    {t("editor.profilePhotoRec")}
                  </p>
                </div>
                <input
                  ref={profileImageRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageSelect(e, "profile")}
                  className="hidden"
                />
              </div>
            </div>

            {/* Cover Image Upload */}
            <div id="upload-cover">
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                {t("editor.coverImage")}
              </label>
              <div
                onClick={() => !uploadProgress && setImageSourcePicker("cover")}
                className={`relative w-full h-48 rounded-xl border-2 border-dashed flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition overflow-hidden bg-gray-50 ${highlightUpload === "cover" ? "border-blue-500 ring-4 ring-blue-200 animate-pulse" : "border-gray-300"}`}
              >
                {profile.cover_image_url ? (
                  <img
                    src={profile.cover_image_url}
                    alt="Cover"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center text-gray-400">
                    <Upload className="w-8 h-8 mb-2" />
                    <span className="text-sm">{t("editor.uploadCoverImage")}</span>
                  </div>
                )}
                {uploadProgress?.type === "cover" && (
                  <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center rounded-xl">
                    <div className="w-3/4 bg-white/20 rounded-full h-2 mb-2">
                      <div className="bg-white h-2 rounded-full transition-all" style={{ width: `${uploadProgress.progress}%` }} />
                    </div>
                    <span className="text-white text-sm font-bold">{uploadProgress.progress}%</span>
                    {uploadProgress.fileSize > 0 && <span className="text-white/70 text-xs mt-0.5">{formatBytes(uploadProgress.loaded || 0)} / {formatBytes(uploadProgress.fileSize)}</span>}
                    <button onClick={(e) => { e.stopPropagation(); cancelUpload(); }} className="mt-1 text-white/80 hover:text-white text-xs underline">{t("editor.cancel")}</button>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1.5">{t("editor.coverImageRec")}</p>
              <input
                ref={coverImageRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleImageSelect(e, "cover")}
                className="hidden"
              />
            </div>

            {/* Welcome Screen */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">
                {t("editor.welcomeScreen")}
              </label>
              <p className="text-xs text-gray-500 mb-3">{t("editor.welcomeScreenDesc")}</p>

              {/* Welcome screen media upload */}
              <div
                onClick={() => !uploadProgress && welcomeScreenRef.current?.click()}
                className="relative w-full h-48 rounded-xl border-2 border-dashed flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition overflow-hidden bg-gray-50 border-gray-300"
              >
                {profile.settings?.welcome_screen?.media_url ? (
                  profile.settings.welcome_screen.media_type === "video" ? (
                    <video src={profile.settings.welcome_screen.media_url} className="w-full h-full object-cover" muted />
                  ) : (
                    <img src={profile.settings.welcome_screen.media_url} alt="Welcome" className="w-full h-full object-cover" />
                  )
                ) : (
                  <div className="flex flex-col items-center text-gray-400">
                    <Film className="w-8 h-8 mb-2" />
                    <span className="text-sm">{t("editor.uploadWelcomeMedia")}</span>
                    <span className="text-xs mt-1 text-gray-400">{t("editor.welcomeMediaFormats")}</span>
                  </div>
                )}
                {uploadProgress?.type === "welcome_screen" && (
                  <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center rounded-xl">
                    <div className="w-3/4 bg-white/20 rounded-full h-2 mb-2">
                      <div className="bg-white h-2 rounded-full transition-all" style={{ width: `${uploadProgress.progress}%` }} />
                    </div>
                    <span className="text-white text-sm font-bold">{uploadProgress.progress}%</span>
                    {uploadProgress.fileSize > 0 && <span className="text-white/70 text-xs mt-0.5">{formatBytes(uploadProgress.loaded || 0)} / {formatBytes(uploadProgress.fileSize)}</span>}
                    <button onClick={(e) => { e.stopPropagation(); cancelUpload(); }} className="mt-1 text-white/80 hover:text-white text-xs underline">{t("editor.cancel")}</button>
                  </div>
                )}
              </div>
              {profile.settings?.welcome_screen?.media_url && (
                <>
                  <div className="flex items-center gap-3 mt-3">
                    <span className="text-sm text-gray-600">{t("editor.welcomeShowWhen")}</span>
                    <select
                      value={profile.settings?.welcome_screen?.display || "once"}
                      onChange={(e) => {
                        const ws = { ...(profile.settings?.welcome_screen || {}), display: e.target.value };
                        setProfile((prev) => ({ ...prev, settings: { ...(prev.settings || {}), welcome_screen: ws } }));
                      }}
                      className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
                    >
                      <option value="always">{t("editor.welcomeAlways")}</option>
                      <option value="once">{t("editor.welcomeOnce")}</option>
                      <option value="scan">{t("editor.welcomeScan")}</option>
                      <option value="never">{t("editor.welcomeNever")}</option>
                    </select>
                  </div>
                  <button
                    onClick={() => {
                      const newSettings = { ...(profile.settings || {}) };
                      delete newSettings.welcome_screen;
                      setProfile((prev) => ({ ...prev, settings: newSettings }));
                    }}
                    className="mt-2 text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> {t("editor.removeWelcomeMedia")}
                  </button>
                </>
              )}
              <input
                ref={welcomeScreenRef}
                type="file"
                accept="image/*,video/mp4,video/webm,video/quicktime"
                onChange={handleWelcomeScreenUpload}
                className="hidden"
              />
            </div>

            {/* Theme Color */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                {t("editor.themeColor")}
              </label>
              {/* Current color preview */}
              <div className="flex items-center gap-3 mb-3 p-3 bg-gray-50 rounded-xl">
                <div
                  className="w-10 h-10 rounded-xl shadow-inner border border-black/10 flex-shrink-0"
                  style={{ backgroundColor: profile.theme_color || "#3B82F6" }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-700">{t("editor.currentColor")}</div>
                  <div className="text-xs font-mono text-gray-400 uppercase">{profile.theme_color || "#3B82F6"}</div>
                </div>
              </div>
              {/* Preset swatches */}
              <div className="flex flex-wrap gap-2 mb-3">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    onClick={() =>
                      setProfile((prev) => ({ ...prev, theme_color: color }))
                    }
                    className={`w-8 h-8 rounded-lg border-2 transition hover:scale-110 ${
                      profile.theme_color === color
                        ? "border-gray-900 ring-2 ring-offset-2 ring-gray-400"
                        : "border-gray-200"
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
              {/* Custom color row */}
              <div className="flex items-center gap-2">
                <label className="relative cursor-pointer">
                  <input
                    type="color"
                    value={profile.theme_color || "#3B82F6"}
                    onChange={(e) =>
                      setProfile((prev) => ({ ...prev, theme_color: e.target.value }))
                    }
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 bg-white rounded-lg hover:bg-gray-50 transition">
                    <Palette className="w-3.5 h-3.5" />
                    {t("editor.customColor")}
                  </div>
                </label>
                <div className="flex items-center border border-gray-200 rounded-lg bg-white overflow-hidden">
                  <span className="px-2 py-1.5 text-xs text-gray-400 bg-gray-50 border-r border-gray-200">#</span>
                  <input
                    type="text"
                    maxLength={6}
                    defaultValue={(profile.theme_color || "#3B82F6").replace("#", "")}
                    key={profile.theme_color}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
                      e.target.value = v;
                      if (v.length === 6) {
                        setProfile((prev) => ({ ...prev, theme_color: `#${v}` }));
                      }
                    }}
                    onBlur={(e) => {
                      const v = e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
                      if (v.length === 6) {
                        setProfile((prev) => ({ ...prev, theme_color: `#${v.toUpperCase()}` }));
                      }
                    }}
                    className="w-[5.5rem] px-2 py-1.5 text-sm font-mono text-gray-700 border-none outline-none uppercase"
                    placeholder="3B82F6"
                  />
                </div>
              </div>
            </div>

            {/* Theme Style */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                {t("editor.themeStyle")}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {THEME_STYLES.map((ts) => {
                  const selected = profile.theme_style === ts.value;
                  const tc = profile.theme_color || "#3B82F6";
                  const previewStyles = {
                    modern: {
                      bg: "bg-gray-50", cover: `linear-gradient(135deg, ${tc}, ${tc}cc)`,
                      avatar: "rounded-full", card: "bg-white rounded-lg shadow-sm", text: "font-sans",
                    },
                    classic: {
                      bg: "bg-gray-100", cover: `linear-gradient(180deg, ${tc}, ${tc}55)`,
                      avatar: "rounded-sm", card: "bg-white rounded-sm border border-gray-200", text: "font-serif",
                    },
                    minimal: {
                      bg: "bg-white", cover: `${tc}11`,
                      avatar: "rounded-full", card: "bg-transparent border-b border-gray-100", text: "font-light",
                    },
                    bold: {
                      bg: "bg-gray-950", cover: `linear-gradient(160deg, ${tc}, #000)`,
                      avatar: "rounded-xl", card: "bg-gray-900 rounded-xl border border-gray-800", text: "font-black text-white",
                    },
                    pro: {
                      bg: "bg-gray-950", cover: `linear-gradient(135deg, ${tc}44, ${tc}11)`,
                      avatar: "rounded-2xl", card: "bg-white/10 rounded-xl border border-white/10", text: "font-bold text-white",
                    },
                    instagram: {
                      bg: "bg-white", cover: `linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)`,
                      avatar: "rounded-full ring-2 ring-pink-500 ring-offset-1", card: "bg-white border-b border-gray-200", text: "font-semibold",
                    },
                    instagram_dark: {
                      bg: "bg-gray-950", cover: `linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)`,
                      avatar: "rounded-full ring-2 ring-pink-500 ring-offset-1 ring-offset-gray-950", card: "bg-gray-950 border-b border-gray-800", text: "font-semibold text-white",
                    },
                  };
                  const p = previewStyles[ts.value] || previewStyles.modern;
                  const isIgThumb = ts.value === "instagram" || ts.value === "instagram_dark";
                  const isIgDarkThumb = ts.value === "instagram_dark";
                  const isProThumb = ts.value === "pro";
                  const isDarkThumb = ["bold", "pro", "instagram_dark"].includes(ts.value);
                  return (
                    <button
                      key={ts.value}
                      onClick={() => setProfile((prev) => ({ ...prev, theme_style: ts.value }))}
                      className={`overflow-hidden rounded-xl border-2 text-left transition flex flex-col justify-between ${
                        selected
                          ? "border-blue-600 ring-1 ring-blue-600"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {/* Mini preview */}
                      <div className={`${p.bg} px-2 pb-2 relative overflow-hidden flex-1`}>
                        {/* Pro mesh blob */}
                        {isProThumb && (
                          <div className="absolute top-0 left-0 w-1/2 h-full rounded-full opacity-20 blur-[15px]" style={{ background: tc }} />
                        )}
                        {/* Cover */}
                        {!isIgThumb && <div className="h-6 rounded-t relative" style={{ background: p.cover }} />}
                        {/* Instagram horizontal layout */}
                        {isIgThumb ? (
                          <div className="relative">
                            <div className="flex items-center gap-1.5 mb-2">
                              <div style={{ background: "linear-gradient(45deg, #f09433, #dc2743, #bc1888)", padding: "1.5px", borderRadius: "9999px" }}>
                                <div className={`w-6 h-6 rounded-full border-[1.5px] ${isIgDarkThumb ? "border-gray-950" : "border-white"}`} style={{ backgroundColor: tc }} />
                              </div>
                              <div>
                                <div className={`w-10 h-1 rounded ${isIgDarkThumb ? "bg-gray-600" : "bg-gray-300"}`} />
                                <div className={`w-7 h-0.5 rounded ${isIgDarkThumb ? "bg-gray-700" : "bg-gray-200"} mt-0.5`} />
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-[1px]">
                              {[1,2,3,4,5,6].map((i) => (
                                <div key={i} className={`aspect-square ${isIgDarkThumb ? "bg-gray-800" : "bg-gray-100"} rounded-[1px]`} />
                              ))}
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex flex-col items-center -mt-3">
                              <div className={`w-6 h-6 ${p.avatar} border-2 border-white`} style={{ backgroundColor: tc }} />
                              <div className={`mt-1 w-12 h-1 rounded ${isDarkThumb ? "bg-gray-600" : "bg-gray-300"}`} />
                              <div className={`mt-1 w-8 h-0.5 rounded ${isDarkThumb ? "bg-gray-700" : "bg-gray-200"}`} />
                            </div>
                            <div className={`mt-2 mx-0.5 p-1.5 ${p.card}`}>
                              <div className={`w-8 h-0.5 rounded mb-1 ${isDarkThumb ? "bg-gray-600" : "bg-gray-200"}`} />
                              <div className={`w-full h-0.5 rounded ${isDarkThumb ? "bg-gray-700" : "bg-gray-100"}`} />
                            </div>
                          </>
                        )}
                      </div>
                      <div className={`py-2 text-center border-t ${selected ? "bg-blue-50 border-blue-100" : "bg-gray-50 border-gray-100"}`}>
                        <div className={`text-sm font-medium ${selected ? "text-blue-700" : "text-gray-700"}`}>{ts.label}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Live Preview */}
              <div className="mt-6">
                <label className="block text-sm font-semibold text-gray-900 mb-3">{t("editor.themePreview")}</label>
                <ThemePreview
                  style={profile.theme_style}
                  color={profile.theme_color || "#3B82F6"}
                  name={profile.name || "Your Name"}
                  tagline={profile.tagline || "Your tagline"}
                  profileImage={profile.profile_image_url}
                />
              </div>
            </div>
          </div>
        )}

        {/* ============================================================
            BLOCKS TAB
            ============================================================ */}
        {activeTab === "blocks" && (
          <div className="space-y-4">
            {/* Header: explainer + add button */}
            <div className="flex items-start justify-between gap-4">
              <p className="text-sm text-gray-500 pt-1">{t("editor.blocksExplainer")}</p>
              <div className="relative flex-shrink-0">
                <button
                  onClick={() =>
                    setAddBlockDropdownOpen(!addBlockDropdownOpen)
                  }
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
                >
                  <Plus className="w-4 h-4" />
                  {t("editor.addBlock")}
                </button>
                {addBlockDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setAddBlockDropdownOpen(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                      {BLOCK_TYPES.map((bt) => {
                        const Icon = bt.icon;
                        const badge = getBlockTierBadge(bt.value);
                        return (
                          <button
                            key={bt.value}
                            onClick={() => handleAddBlock(bt.value)}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <Icon className="w-4 h-4 text-gray-400" />
                            {bt.label}
                            {badge && (
                              <span className="ml-auto inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold" style={{ backgroundColor: badge.color + "20", color: badge.color }}>
                                {badge.label.toUpperCase()}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Tier warning */}
            {blocks.some((b) => getBlockTierBadge(b.block_type)) && (
              <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
                <Lock className="w-4 h-4 flex-shrink-0" />
                {t("editor.plusBlocksWarning")}
              </div>
            )}

            {/* Block List */}
            {blocks.length === 0 && (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                <Type className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">{t("editor.noBlocks")}</p>
                <p className="text-gray-400 text-xs mt-1">
                  {t("editor.noBlocksDesc")}
                </p>
              </div>
            )}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                {blocks.map((block) => {
                  const isExpanded = expandedBlocks[block.id];
                  const typeLabel =
                    BLOCK_TYPES.find((bt) => bt.value === block.block_type)
                      ?.label || block.block_type;

                  return (
                    <SortableBlock key={block.id} id={block.id}>
                      {(dragHandleProps) => (
                        <>
                          {/* Block Header */}
                          <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 border-b border-gray-100">
                            <button
                              {...dragHandleProps}
                              className="cursor-grab active:cursor-grabbing p-0.5 text-gray-300 hover:text-gray-500 flex-shrink-0 touch-none"
                            >
                              <GripVertical className="w-4 h-4" />
                            </button>

                            {/* Title (editable, per language) */}
                            <div className="flex-1 min-w-0 flex items-center gap-1.5">
                              <Pencil className="w-3 h-3 text-gray-300 flex-shrink-0" />
                              {["en", "es"].map((lang) => (
                                <label key={lang} className="flex-1 min-w-0 flex items-center gap-1 cursor-text">
                                  <span className="text-[10px] font-bold text-gray-300 uppercase flex-shrink-0">{lang}</span>
                                  <input
                                    type="text"
                                    value={(block.config?.title_i18n || {})[lang] || ""}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setBlocks((prev) =>
                                        prev.map((b) =>
                                          b.id === block.id
                                            ? { ...b, config: { ...b.config, title_i18n: { ...(b.config?.title_i18n || {}), [lang]: val } } }
                                            : b
                                        )
                                      );
                                    }}
                                    onBlur={() =>
                                      handleUpdateBlock(block.id, { config: { ...block.config, title_i18n: block.config?.title_i18n || {} } })
                                    }
                                    placeholder={typeLabel}
                                    className="flex-1 min-w-0 text-sm font-medium text-gray-900 bg-transparent border-none outline-none focus:ring-1 focus:ring-blue-500 rounded px-1"
                                  />
                                </label>
                              ))}
                            </div>

                            {getBlockWarning(block) && (
                              <span title={t("editor.blockIncomplete")} className="text-amber-500 flex-shrink-0">
                                <AlertTriangle className="w-4 h-4" />
                              </span>
                            )}

                            {/* Expand/Collapse — always visible */}
                            <button
                              onClick={() => toggleBlockExpanded(block.id)}
                              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded flex-shrink-0"
                            >
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>

                            {/* Actions row — wraps on mobile */}
                            <div className="flex items-center gap-1 w-full sm:w-auto sm:ml-auto order-last sm:order-none">
                              {/* Type Badge */}
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full flex-shrink-0">
                                {typeLabel}
                              </span>
                              {(() => {
                                const badge = getBlockTierBadge(block.block_type);
                                return badge ? (
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0" style={{ backgroundColor: badge.color + "20", color: badge.color }}>
                                    <Lock className="w-3 h-3" />
                                    {badge.label.toUpperCase()}
                                  </span>
                                ) : null;
                              })()}

                              <div className="ml-auto sm:ml-0 flex items-center gap-0.5">
                                {/* Visibility Toggle */}
                                <button
                                  onClick={() =>
                                    handleUpdateBlock(block.id, {
                                      is_visible: !block.is_visible,
                                    })
                                  }
                                  className={`p-1.5 rounded transition ${
                                    block.is_visible !== false
                                      ? "text-green-600 hover:bg-green-50"
                                      : "text-gray-300 hover:bg-gray-50"
                                  }`}
                                  title={
                                    block.is_visible !== false ? t("editor.visible") : t("editor.hidden")
                                  }
                                >
                                  <Eye className="w-4 h-4" />
                                </button>

                                {/* Delete */}
                                <button
                                  onClick={() => handleDeleteBlock(block.id)}
                                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Block Body */}
                          {isExpanded && (
                            <div className="p-4">
                              {getBlockTierBadge(block.block_type) && (
                                <div className="mb-3 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
                                  <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                                  {t("editor.plusBlockWarning")}
                                </div>
                              )}
                              {renderBlockEditor(block)}
                            </div>
                          )}
                        </>
                      )}
                    </SortableBlock>
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* ============================================================
            GALLERY TAB
            ============================================================ */}
        {activeTab === "gallery" && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">{t("editor.galleriesDesc")}</p>
            {/* Create Gallery */}
            <div className="flex justify-end gap-2">
              {showNewGalleryInput ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newGalleryTitle}
                    onChange={(e) => setNewGalleryTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreateGallery();
                      if (e.key === "Escape") {
                        setShowNewGalleryInput(false);
                        setNewGalleryTitle("");
                      }
                    }}
                    placeholder={t("editor.placeholder.galleryTitle")}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                  <button
                    onClick={handleCreateGallery}
                    className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
                  >
                    {t("editor.create")}
                  </button>
                  <button
                    onClick={() => {
                      setShowNewGalleryInput(false);
                      setNewGalleryTitle("");
                    }}
                    className="px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition"
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowNewGalleryInput(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
                >
                  <Plus className="w-4 h-4" />
                  {t("editor.createGallery")}
                </button>
              )}
            </div>

            {/* Gallery List */}
            {galleries.length === 0 && (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                <Image className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">{t("editor.noGalleries")}</p>
                <p className="text-gray-400 text-xs mt-1">
                  {t("editor.noGalleriesDesc")}
                </p>
              </div>
            )}

            {galleries.map((gallery) => {
              const isExpanded = expandedGalleries[gallery.id];
              const mediaItems =
                gallery.media_items || gallery.items || [];

              return (
                <div
                  key={gallery.id}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                >
                  {/* Gallery Header */}
                  <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100">
                    <Image className="w-4 h-4 text-gray-400 flex-shrink-0" />

                    <input
                      type="text"
                      value={gallery.title || ""}
                      onChange={(e) => {
                        setGalleries((prev) =>
                          prev.map((g) =>
                            g.id === gallery.id
                              ? { ...g, title: e.target.value }
                              : g
                          )
                        );
                      }}
                      onBlur={() =>
                        handleUpdateGallery(gallery.id, {
                          title: gallery.title,
                        })
                      }
                      className="flex-1 min-w-[100px] text-sm font-medium text-gray-900 bg-transparent border-none outline-none focus:ring-1 focus:ring-blue-500 rounded px-1"
                    />

                    <div className="flex items-center gap-1 ml-auto">
                      <span className="text-xs text-gray-400 mr-1">
                        {mediaItems.length} item{mediaItems.length !== 1 ? "s" : ""}
                      </span>

                      {/* Visibility Toggle */}
                      <button
                        onClick={() =>
                          handleUpdateGallery(gallery.id, {
                            is_visible: !gallery.is_visible,
                          })
                        }
                        className={`p-1.5 rounded transition ${
                          gallery.is_visible !== false
                            ? "text-green-600 hover:bg-green-50"
                            : "text-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>

                      {/* Delete */}
                      <button
                        onClick={() => handleDeleteGallery(gallery.id)}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>

                      {/* Expand/Collapse */}
                      <button
                        onClick={() => toggleGalleryExpanded(gallery.id)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Gallery Body */}
                  {isExpanded && (
                    <div className="p-4">
                      {/* Upload / Camera Buttons */}
                      <div className="mb-4">
                        <input
                          ref={(el) => {
                            galleryUploadRefs.current[gallery.id] = el;
                          }}
                          type="file"
                          accept="image/*,video/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const isVideo = file.type.startsWith("video/");
                            const mediaType = isVideo ? "video" : "image";
                            handleUploadMedia(gallery.id, file, mediaType);
                            e.target.value = "";
                          }}
                          className="hidden"
                        />
                        <input
                          ref={(el) => {
                            galleryCameraRefs.current[gallery.id] = el;
                          }}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            handleUploadMedia(gallery.id, file, "image");
                            e.target.value = "";
                          }}
                          className="hidden"
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            onClick={() =>
                              galleryUploadRefs.current[gallery.id]?.click()
                            }
                            className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                          >
                            <Upload className="w-4 h-4" />
                            {t("editor.uploadMedia")}
                          </button>
                          <button
                            onClick={() =>
                              galleryCameraRefs.current[gallery.id]?.click()
                            }
                            className="flex items-center gap-2 px-3 py-2 text-sm text-green-600 border border-green-200 bg-green-50 hover:bg-green-100 rounded-lg transition"
                          >
                            <Camera className="w-4 h-4" />
                            {t("editor.takePhoto")}
                          </button>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          {storage
                            ? t("editor.uploadLimits", { max: maxUploadMb, free: Math.max(0, Math.floor(storage.max_mb - storage.used_mb)) })
                            : t("editor.uploadLimitsNoStorage", { max: maxUploadMb })}
                        </p>
                      </div>

                      {/* Media Grid */}
                      {mediaItems.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-8">
                          {t("editor.noMedia")}
                        </p>
                      ) : (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleMediaDragEnd(gallery.id, e)}>
                          <SortableContext items={mediaItems.map((m) => m.id)} strategy={rectSortingStrategy}>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                              {mediaItems.map((item) => (
                                <SortableMediaItem key={item.id} id={item.id}>
                                  {({ dragListeners }) => (
                                    <div className="rounded-lg overflow-hidden border border-gray-200 bg-white">
                                      <div className="relative group aspect-square bg-gray-100">
                                        {item.media_type === "video" ? (
                                          <div className="relative w-full h-full bg-gray-800">
                                            {item.thumbnail_url ? (
                                              <img src={item.thumbnail_url} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                              <video src={item.file_url || item.url} muted preload="metadata" className="w-full h-full object-cover" />
                                            )}
                                            <div className="absolute inset-0 flex items-center justify-center">
                                              <Video className="w-8 h-8 text-white/80 drop-shadow" />
                                            </div>
                                          </div>
                                        ) : (
                                          <img
                                            src={item.thumbnail_url || item.url || item.file_url}
                                            alt=""
                                            draggable={false}
                                            className="w-full h-full object-cover"
                                          />
                                        )}
                                        {/* Drag handle */}
                                        <div
                                          {...dragListeners}
                                          className="absolute top-1 left-1 p-1.5 rounded-full bg-white/90 text-gray-600 hover:bg-white cursor-grab active:cursor-grabbing z-10 shadow-sm"
                                          style={{ touchAction: "none" }}
                                        >
                                          <GripVertical className="w-3.5 h-3.5" />
                                        </div>
                                        {/* Action buttons */}
                                        <div className="absolute top-1 right-1 flex gap-1">
                                          <button
                                            onClick={() => setEditingMediaItem({ galleryId: gallery.id, item })}
                                            className="p-1.5 rounded-full transition opacity-70 sm:opacity-0 group-hover:opacity-100 bg-white/90 text-gray-600 hover:bg-white"
                                          >
                                            <Type className="w-3 h-3" />
                                          </button>
                                          <button
                                            onClick={() => handleDeleteMedia(gallery.id, item.id, item.media_type)}
                                            className="p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition opacity-70 sm:opacity-0 group-hover:opacity-100"
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        </div>
                                        {/* Type badge */}
                                        {item.media_type && item.media_type !== "image" && (
                                          <span className="absolute top-1 left-1 mt-8 px-1.5 py-0.5 bg-black/60 text-white text-[10px] rounded">
                                            {item.media_type === "video" ? "Video" : "360"}
                                          </span>
                                        )}
                                        {/* Caption/link indicator */}
                                        {(item.title || item.link_url) && (
                                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
                                            <p className="text-white text-[10px] truncate">{item.title}</p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </SortableMediaItem>
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ============================================================
            SHARE TAB
            ============================================================ */}
        {activeTab === "share" && (
          <div className="space-y-8">
            {/* Profile URL with Referrer Source */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                {t("editor.profileUrl")}
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                {t("editor.referrerSourceDesc")}
              </p>

              {/* Referrer source selector */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">{t("editor.referrerSource")}</label>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { value: "", tKey: "editor.referrerNone" },
                    { value: "whatsapp", tKey: "editor.referrerWhatsapp" },
                    { value: "instagram", tKey: "editor.referrerInstagram" },
                    { value: "facebook", tKey: "editor.referrerFacebook" },
                    { value: "email", tKey: "editor.referrerEmail" },
                    { value: "flyer", tKey: "editor.referrerFlyer" },
                  ].map((ref) => (
                    <button
                      key={ref.value}
                      onClick={() => setSelectedRef(ref.value)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition ${
                        selectedRef === ref.value
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {t(ref.tKey)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Generated URL */}
              <div className="flex items-center gap-2">
                <div className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 truncate font-mono">
                  {trackedUrl}
                </div>
                <button
                  onClick={handleCopyUrl}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition ${
                    urlCopied
                      ? "bg-green-100 text-green-700 border border-green-200"
                      : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {urlCopied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                  {urlCopied ? t("editor.copied") : t("editor.copy")}
                </button>
                <a
                  href={trackedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t("editor.open")}
                </a>
              </div>
            </div>

            {/* QR Code Preview */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                {t("editor.qrCode")}
              </h3>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm relative">
                  <QRCodeSVG
                    value={`${profileUrl}?ref=qr`}
                    size={180}
                    level="H"
                    includeMargin
                    fgColor={profile.settings?.qr_use_color ? profile.theme_color : "#000000"}
                  />
                  {profile.settings?.qr_show_logo && profile.profile_image_url && (
                    <img
                      src={profile.profile_image_url}
                      alt=""
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full border-[4px] border-white shadow-sm object-cover"
                    />
                  )}
                </div>
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-gray-500">
                    {t("editor.qrScanDesc")}
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!profile.settings?.qr_show_logo}
                      onChange={async (e) => {
                        const newSettings = { ...(profile.settings || {}), qr_show_logo: e.target.checked };
                        setProfile((prev) => ({ ...prev, settings: newSettings }));
                        try {
                          await updateProfile(id, { settings: newSettings });
                        } catch (err) {
                          showToast(t("editor.toast.profileSaveFailed"), "error");
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{t("editor.qrShowLogo")}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!profile.settings?.qr_use_color}
                      onChange={async (e) => {
                        const newSettings = { ...(profile.settings || {}), qr_use_color: e.target.checked };
                        setProfile((prev) => ({ ...prev, settings: newSettings }));
                        try {
                          await updateProfile(id, { settings: newSettings });
                        } catch (err) {
                          showToast(t("editor.toast.profileSaveFailed"), "error");
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{t("editor.qrUseColor")}</span>
                  </label>
                  <button
                    onClick={() => setQrModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition"
                  >
                    <Download className="w-4 h-4" />
                    {t("editor.downloadQr")}
                  </button>
                </div>
              </div>
            </div>

            {/* vCard Download */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t("editor.vcard")}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {t("editor.vcardDesc")}
              </p>
              <button
                onClick={handleDownloadVCard}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition"
              >
                <Download className="w-4 h-4" />
                {t("editor.downloadVcardFile")}
              </button>
            </div>

            {/* NFC Cards */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t("editor.nfcCards")}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {t("editor.nfcCardsDesc")}
              </p>

              {/* Scan NFC button */}
              <button
                onClick={openScanModal}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 mb-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition"
              >
                <Smartphone className="h-4 w-4" />
                {t("editor.scanNfc")}
              </button>

              {/* Manual code input */}
              <div className="relative mb-4">
                <p className="text-xs text-gray-400 mb-2">{t("editor.orEnterCode")}</p>
              </div>
              <form onSubmit={handleLinkCard} className="flex items-center gap-2 mb-4">
                <div className="relative flex-1">
                  <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={cardCode}
                    onChange={(e) => { setCardCode(e.target.value); setCardError(""); }}
                    placeholder={t("editor.placeholder.cardCode")}
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none font-mono uppercase"
                  />
                </div>
                <button
                  type="submit"
                  disabled={cardLinking || !cardCode.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition"
                >
                  {cardLinking ? t("editor.linking") : t("editor.linkCard")}
                </button>
              </form>
              {cardError && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2 mb-4">
                  {cardError}
                </p>
              )}

              {/* Linked cards list */}
              {linkedCards.length > 0 ? (
                <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                  {linkedCards.map((card) => (
                    <div key={card.id} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <CreditCard className="h-4 w-4 text-gray-400" />
                        <div>
                          <code className="text-sm font-mono font-semibold text-gray-800">
                            {card.code}
                          </code>
                          {card.label && (
                            <span className="ml-2 text-xs text-gray-400">{card.label}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400">
                          {card.scan_count} scan{card.scan_count !== 1 ? "s" : ""}
                        </span>
                        <button
                          onClick={() => handleUnlinkCard(card.code)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                          title="Unlink card"
                        >
                          <Unlink className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-lg">
                  {t("editor.noCards")}
                </p>
              )}
            </div>

            {/* Social Preview — mirrors backend/app/routes/seo.py */}
            {(() => {
              const seoMinPos = featureTiers["seo"] ?? 1;
              const seoActive = profileTierPos >= seoMinPos;
              const seoTier = tiersByPosition[seoMinPos];
              const name = profile.name || t("editor.profile");
              // SEO plans: profile name, tagline/description, profile image (or cover)
              // Free: "Name - app name", the app's default description and icon
              const title = seoActive ? name : `${name} - ${appName}`;
              const desc = seoActive
                ? (profile.tagline || profile.description || "")
                : (process.env.REACT_APP_META_DESCRIPTION || "");
              const image = seoActive
                ? (profile.profile_image_url || profile.cover_image_url)
                : "/favicon.png";
              const wide = seoActive && !profile.profile_image_url && !!profile.cover_image_url;
              return (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">
                    {t("editor.socialPreview")}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    {t("editor.socialPreviewDesc")}
                  </p>
                  <div className="max-w-md rounded-xl overflow-hidden border border-gray-200 bg-[#f0f2f5] shadow-sm">
                    {wide && image && (
                      <img src={image} alt="" className="w-full aspect-[1.91/1] object-cover" />
                    )}
                    <div className="flex gap-3 p-3">
                      {!wide && image && (
                        <img
                          src={image}
                          alt=""
                          className={`flex-shrink-0 w-16 h-16 rounded-md bg-white ${seoActive ? "object-cover" : "object-contain p-1"}`}
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 line-clamp-2">{title}</p>
                        {desc && <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">{desc}</p>}
                        <p className="text-xs text-gray-400 truncate mt-1">{window.location.host}</p>
                      </div>
                    </div>
                  </div>
                  {seoActive ? (
                    <p className="text-xs text-gray-400 mt-3">{t("editor.socialPreviewSeoHint")}</p>
                  ) : (
                    <div className="mt-4 flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                      <Lock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-amber-800">
                          {t("editor.socialPreviewUpgrade", { appName, tier: seoTier?.label || "Pro" })}
                        </p>
                        <button
                          onClick={() => navigate("/app/subscriptions")}
                          className="mt-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition"
                        >
                          {t("editor.socialPreviewUpgradeBtn")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* ==================== APPLICATION TAB ==================== */}
        {activeTab === "app" && (
          <div className="space-y-6">
            {profileTierPos < (featureTiers["pwa"] ?? 1) ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <Lock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900">{t("editor.app.plusRequired")}</h3>
                <p className="mt-2 text-sm text-gray-500">{t("editor.app.plusRequiredDesc")}</p>
              </div>
            ) : (
              <>
                {/* Enable PWA */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{t("editor.app.enablePwa")}</h3>
                      <p className="mt-0.5 text-xs text-gray-500">{t("editor.app.enablePwaDesc")}</p>
                    </div>
                    <button
                      onClick={async () => {
                        const newVal = !profile.settings?.pwa_enabled;
                        const newSettings = { ...(profile.settings || {}), pwa_enabled: newVal };
                        setProfile((prev) => ({ ...prev, settings: newSettings }));
                        try {
                          await updateProfile(id, { settings: newSettings });
                          showToast(newVal ? t("editor.app.enabled") : t("editor.app.disabled"));
                        } catch {
                          showToast(t("editor.toast.profileSaveFailed"), "error");
                        }
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        profile.settings?.pwa_enabled ? "bg-blue-600" : "bg-gray-300"
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        profile.settings?.pwa_enabled ? "translate-x-6" : "translate-x-1"
                      }`} />
                    </button>
                  </div>
                </div>

                {profile.settings?.pwa_enabled && (
                  <>
                    {/* App Icon */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">{t("editor.app.icon")}</h3>
                      <p className="text-xs text-gray-500 mb-4">{t("editor.app.iconDesc")}</p>
                      <div className="flex items-center gap-5">
                        <div
                          onClick={() => !uploadProgress && pwaIconRef.current?.click()}
                          className="relative w-20 h-20 border-2 border-dashed border-gray-300 hover:border-blue-400 cursor-pointer flex items-center justify-center overflow-hidden transition group"
                          style={{ borderRadius: "22%", WebkitBorderRadius: "22%" }}
                        >
                          {(profile.settings?.pwa_icon || profile.profile_image_url) ? (
                            <img
                              src={profile.settings?.pwa_icon || profile.profile_image_url}
                              alt=""
                              className="w-full h-full object-cover"
                              style={{ borderRadius: "22%" }}
                            />
                          ) : (
                            <Upload className="h-6 w-6 text-gray-400 group-hover:text-blue-500 transition" />
                          )}
                          {uploadProgress?.type === "pwa_icon" ? (
                            <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center" style={{ borderRadius: "22%" }}>
                              <span className="text-white text-xs font-bold">{uploadProgress.progress}%</span>
                              {uploadProgress.fileSize > 0 && <span className="text-white/70 text-[9px]">{formatBytes(uploadProgress.loaded || 0)} / {formatBytes(uploadProgress.fileSize)}</span>}
                              <button onClick={(e) => { e.stopPropagation(); cancelUpload(); }} className="mt-0.5 text-white/80 hover:text-white text-[10px] underline">{t("editor.cancel")}</button>
                            </div>
                          ) : (
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition" style={{ borderRadius: "22%" }} />
                          )}
                        </div>
                        <input
                          ref={pwaIconRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageSelect(e, "pwa_icon")}
                        />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">{t("editor.app.iconHint")}</p>
                          {profile.settings?.pwa_icon && (
                            <button
                              onClick={async () => {
                                const newSettings = { ...(profile.settings || {}), pwa_icon: null };
                                setProfile((prev) => ({ ...prev, settings: newSettings }));
                                try {
                                  await updateProfile(id, { settings: newSettings });
                                  showToast(t("editor.app.iconReset"));
                                } catch {
                                  showToast(t("editor.toast.profileSaveFailed"), "error");
                                }
                              }}
                              className="mt-2 text-xs text-red-500 hover:text-red-700 transition"
                            >
                              {t("editor.app.iconRemove")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* App Name */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">{t("editor.app.name")}</h3>
                      <p className="text-xs text-gray-500 mb-3">{t("editor.app.nameDesc")}</p>
                      <input
                        type="text"
                        value={profile.settings?.pwa_name || ""}
                        onChange={(e) =>
                          setProfile((prev) => ({
                            ...prev,
                            settings: { ...(prev.settings || {}), pwa_name: e.target.value },
                          }))
                        }
                        onBlur={async () => {
                          try {
                            await updateProfile(id, { settings: profile.settings });
                          } catch {
                            showToast(t("editor.toast.profileSaveFailed"), "error");
                          }
                        }}
                        placeholder={profile.name || t("editor.app.namePlaceholder")}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* Preview */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">{t("editor.app.preview")}</h3>
                      <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                        <div className="flex-shrink-0">
                          {(profile.settings?.pwa_icon || profile.profile_image_url) ? (
                            <img
                              src={profile.settings?.pwa_icon || profile.profile_image_url}
                              alt=""
                              className="w-14 h-14 object-cover shadow-sm"
                              style={{ borderRadius: "22%" }}
                            />
                          ) : (
                            <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-blue-400 to-purple-500 text-white text-xl font-bold shadow-sm" style={{ borderRadius: "22%" }}>
                              {profile.name?.charAt(0)?.toUpperCase() || "?"}
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {profile.settings?.pwa_name || profile.name || "Profile"}
                          </p>
                          <p className="text-xs text-gray-400">{window.location.host}</p>
                        </div>
                      </div>
                    </div>

                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* NFC / QR Scan Modal */}
      {showScanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeScanModal}
          />
          <div className="relative w-full max-w-sm rounded-2xl bg-white shadow-xl mx-4 overflow-hidden">
            {/* Tab switcher */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => { stopQrScanner(); setScanMode("nfc"); }}
                className={`flex-1 px-4 py-3 text-sm font-medium transition ${
                  scanMode === "nfc"
                    ? "text-indigo-600 border-b-2 border-indigo-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <Smartphone className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                {t("editor.nfcTap")}
              </button>
              <button
                onClick={() => { setScanMode("qr"); startQrScanner(); }}
                className={`flex-1 px-4 py-3 text-sm font-medium transition ${
                  scanMode === "qr"
                    ? "text-indigo-600 border-b-2 border-indigo-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <QrCode className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                {t("editor.scanQr")}
              </button>
            </div>

            <div className="p-8">
              {scanMode === "nfc" ? (
                <div className="text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-indigo-50">
                    <Smartphone className="h-8 w-8 text-indigo-500" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {t("editor.tapNfcCard")}
                  </h3>
                  <div className="text-sm text-gray-500 space-y-3 mb-6 text-left">
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-xs font-bold flex items-center justify-center">1</span>
                      <p>{t("editor.nfcStep1")}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-xs font-bold flex items-center justify-center">2</span>
                      <p>{t("editor.nfcStep2")}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-xs font-bold flex items-center justify-center">3</span>
                      <p>{t("editor.nfcStep3")}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="relative rounded-xl overflow-hidden bg-black mb-4 aspect-square">
                    <video
                      ref={videoRef}
                      className="w-full h-full object-cover"
                      playsInline
                      muted
                    />
                    {qrScanning && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-48 h-48 border-2 border-white/60 rounded-2xl" />
                      </div>
                    )}
                    {!qrScanning && !qrError && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-white border-t-transparent" />
                      </div>
                    )}
                  </div>
                  {qrError ? (
                    <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2 mb-4">
                      {qrError}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500 mb-4">
                      {t("editor.qrScanCamera")}
                    </p>
                  )}
                </div>
              )}

              <button
                onClick={closeScanModal}
                className="w-full rounded-lg px-5 py-2.5 text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 transition"
              >
                {t("editor.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media Edit Modal */}
      {editingMediaItem && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setEditingMediaItem(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              {editingMediaItem.item.media_type === "video" ? (
                <div className="w-full h-48 flex items-center justify-center bg-gray-800">
                  <Video className="w-12 h-12 text-white/60" />
                </div>
              ) : (
                <img
                  src={editingMediaItem.item.thumbnail_url || editingMediaItem.item.url || editingMediaItem.item.file_url}
                  alt=""
                  className="w-full h-48 object-cover"
                />
              )}
              <button
                onClick={() => setEditingMediaItem(null)}
                className="absolute top-2 right-2 p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 transition"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("editor.placeholder.caption")}</label>
                <input
                  type="text"
                  value={editingMediaItem.item.title || ""}
                  onChange={(e) => setEditingMediaItem((prev) => ({ ...prev, item: { ...prev.item, title: e.target.value } }))}
                  placeholder={t("editor.placeholder.caption")}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("editor.placeholder.linkUrl")}</label>
                <input
                  type="url"
                  value={editingMediaItem.item.link_url || ""}
                  onChange={(e) => {
                    const url = e.target.value;
                    setEditingMediaItem((prev) => ({
                      ...prev,
                      item: { ...prev.item, link_url: url, ...(!url && { link_text: "" }) },
                    }));
                  }}
                  placeholder="https://..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              {editingMediaItem.item.link_url && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("editor.placeholder.linkText")}</label>
                  <input
                    type="text"
                    value={editingMediaItem.item.link_text || ""}
                    onChange={(e) => setEditingMediaItem((prev) => ({ ...prev, item: { ...prev.item, link_text: e.target.value } }))}
                    placeholder={t("editor.placeholder.linkText")}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setEditingMediaItem(null)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  {t("editor.cancel")}
                </button>
                <button
                  onClick={() => {
                    const { galleryId, item } = editingMediaItem;
                    const linkText = item.link_url ? (item.link_text || "") : "";
                    handleUpdateMediaItem(galleryId, item.id, { title: item.title || "", link_url: item.link_url || "", link_text: linkText });
                    setGalleries((prev) =>
                      prev.map((g) => {
                        if (g.id !== galleryId) return g;
                        const upd = (arr) => (arr || []).map((m) => m.id === item.id ? { ...m, title: item.title, link_url: item.link_url, link_text: linkText } : m);
                        return { ...g, media_items: upd(g.media_items), items: upd(g.items) };
                      })
                    );
                    setEditingMediaItem(null);
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
                >
                  {t("editor.save")}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {uploadProgress?.type === "media" && (
        <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white/95 rounded-3xl shadow-2xl p-8 w-96 max-w-[92vw] border border-white/20">
            <div className="flex flex-col items-center mb-6">
              <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-3">
                <Upload className="w-6 h-6 text-blue-500 animate-pulse" />
              </div>
              <p className="text-base font-semibold text-gray-900">{t("editor.uploading")}</p>
              <p className="text-xs text-gray-400 mt-1 truncate max-w-full">{uploadProgress.fileName}</p>
            </div>

            <div className="relative mb-2">
              <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                <div
                  className="h-2.5 rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: `${uploadProgress.progress}%`,
                    background: "linear-gradient(90deg, #3b82f6, #6366f1)",
                  }}
                />
              </div>
            </div>
            <p className="text-center text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              {uploadProgress.progress}%
            </p>
            {uploadProgress.fileSize > 0 && (
              <p className="text-center text-xs text-gray-400 mt-1 mb-6">
                {formatBytes(uploadProgress.loaded || 0)} / {formatBytes(uploadProgress.fileSize)}
              </p>
            )}

            <button
              onClick={() => { uploadAbortRef.current?.abort(); }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-gray-500 text-sm font-medium rounded-xl hover:bg-gray-100 transition"
            >
              <XIcon className="w-3.5 h-3.5" />
              {t("crm.cancel") || "Cancel"}
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        message={confirmModal}
        confirmLabel={t("crm.deleteConfirmBtn") || "Delete"}
        cancelLabel={t("crm.cancel") || "Cancel"}
        onConfirm={() => { setConfirmModal(null); confirmRef.current?.(); }}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
}

